/**
 * The attendance contract — punch logging, monthly ledgers, shifts, and regularisations.
 *
 * Adheres to repository invariants:
 * - DTOs distinct from Prisma models to avoid leaking internal columns.
 * - ResourceTarget fields (orgId, personId) attached for authorisation.
 * - Timestamps in UTC on the wire, resolving in Location.timezone on the server.
 * - Regularisations raise approval workflows in @hrms/workflow.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import {
  attendanceRuleKindSchema,
  attendanceRulePolicyDtoSchema,
  saveAttendanceRulePolicyBodySchema,
} from '../schemas/attendance-rules.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared shift-policy field schemas
// ---------------------------------------------------------------------------

/** 0 = Sunday, matching `ZonedParts.weekday` in @hrms/shared and `packages/attendance`'s `weekdayOf`. */
const weeklyOffDaysSchema = z.array(z.number().int().min(0).max(6)).max(7);

/**
 * Nth-weekday-of-month offs, e.g. `{"6": [2, 4]}` = 2nd and 4th Saturday — the common Indian
 * six-day-week pattern. Keys are weekday strings "0"-"6"; values are 1-5 (a month has at most five
 * occurrences of any given weekday), matching `packages/attendance/src/calc/week.ts`'s
 * `nthWeekdayOfMonth` (`Math.ceil(day / 7)`).
 */
const alternateOffDaysSchema = z
  .record(z.string(), z.array(z.number().int().min(1).max(5)).min(1))
  .nullable()
  .refine((val) => !val || Object.keys(val).every((key) => /^[0-6]$/.test(key)), {
    message: 'alternateOffDays keys must be weekday numbers 0-6 (as strings)',
  });

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * One early-exit penalty tier: leaving at least `afterMinutes` early (after the shift's grace) costs
 * `penaltyDays`. A decimal STRING — never a float — because payroll consumes it (CLAUDE.md invariant 2).
 */
export const earlyExitPenaltyRuleSchema = z.object({
  afterMinutes: z.number().int().min(1).max(480),
  penaltyDays: z
    .string()
    .regex(
      /^(0(\.\d{1,2})?|1(\.0{1,2})?)$/,
      'Use a decimal between 0.01 and 1 with at most 2 places',
    )
    .refine((v) => Number(v) > 0, 'Penalty must be greater than 0'),
});
export type EarlyExitPenaltyRuleDto = z.infer<typeof earlyExitPenaltyRuleSchema>;

/** At most 5 tiers, strictly ascending by minutes, penalties never decreasing as the exit gets earlier. */
export const earlyExitPenaltyRulesSchema = z
  .array(earlyExitPenaltyRuleSchema)
  .max(5)
  .superRefine((rules, ctx) => {
    for (let i = 1; i < rules.length; i++) {
      const prev = rules[i - 1]!;
      const cur = rules[i]!;
      if (cur.afterMinutes <= prev.afterMinutes) {
        ctx.addIssue({
          code: 'custom',
          message: 'Tiers must be in ascending order of minutes',
          path: [i, 'afterMinutes'],
        });
      }
      if (Number(cur.penaltyDays) < Number(prev.penaltyDays)) {
        ctx.addIssue({
          code: 'custom',
          message: 'A later tier cannot cost less than an earlier one',
          path: [i, 'penaltyDays'],
        });
      }
    }
  });

export const shiftDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  startTime: z.string(), // e.g. "09:00"
  endTime: z.string(), // e.g. "18:00"
  breakMinutes: z.number().int().default(60),
  /** Extra minutes beyond breakMinutes before EXCESSIVE_BREAK is flagged. Null = flag at breakMinutes. */
  breakWarningMinutes: z.number().int().nullable().default(null),
  graceMinutes: z.number().int().default(15),
  /** Minutes of lead time the attendance-shift-reminder sweep gives before this shift's
   * start/end when notifying an employee — see Shift.reminderLeadMinutes. */
  reminderLeadMinutes: z.number().int().default(15),
  halfDayThresholdHours: z.number().default(4),
  fullDayThresholdHours: z.number().default(8),
  /** Minutes of early departure forgiven before it counts. 0 = every minute counts. */
  earlyExitGraceMinutes: z.number().int().default(0),
  /** Null = no early-exit penalty. See earlyExitPenaltyRulesSchema. */
  earlyExitPenaltyRules: earlyExitPenaltyRulesSchema.nullable().default(null),
  /** Minutes early (post-grace) at which a WARNING fires — always strictly below the first penalty
   * tier when both are configured. Null = no warning configured. */
  earlyExitWarningMinutes: z.number().int().min(1).max(480).nullable().default(null),
  /** Minutes before shift start a clock-IN is refused. Null = no restriction (legacy default). */
  earlyCheckInGuardMinutes: z.number().int().min(0).max(720).nullable().default(null),
  /** Minutes past shift end at which the auto-clock-out sweep force-closes an open IN with a
   * system-generated OUT punch. Null = disabled (the default — the day stays open until MISSING_OUT
   * or a real punch). */
  autoClockOutMinutes: z.number().int().min(0).max(720).nullable().default(null),
  isNightShift: z.boolean().default(false),
  isActive: z.boolean().default(true),
  weeklyOffDays: weeklyOffDaysSchema.default([0]),
  alternateOffDays: alternateOffDaysSchema.default(null),
});
export type ShiftDto = z.infer<typeof shiftDtoSchema>;

/**
 * A shift as the management screen needs it, which is more than the picker DTO carries.
 *
 * `isCrossMidnight` is derived from the TIMES (`endTime <= startTime`), not from the `isNightShift`
 * flag: the flag is a reporting label an admin sets, while the times are what actually decide
 * whether the shift runs into the next day — the same rule `shiftWindowMinutes` applies server-side.
 */
export const shiftManagementRowDtoSchema = shiftDtoSchema.extend({
  isCrossMidnight: z.boolean(),
  /** Paid working span in minutes: the shift window minus its break. */
  durationMinutes: z.number().int(),
  /** Employees whose standing ShiftAssignment currently points at this shift. */
  assignedEmployeeCount: z.number().int(),
  updatedAt: instantSchema,
});
export type ShiftManagementRowDto = z.infer<typeof shiftManagementRowDtoSchema>;

export const shiftManagementSummaryDtoSchema = z.object({
  total: z.number().int(),
  active: z.number().int(),
  inactive: z.number().int(),
  crossMidnight: z.number().int(),
});
export type ShiftManagementSummaryDto = z.infer<typeof shiftManagementSummaryDtoSchema>;

export const shiftAssignmentDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  employeeCode: z.string().optional(),
  departmentName: z.string().optional(),
  locationName: z.string().optional(),
  designation: z.string().optional(),
  shiftId: uuidSchema,
  shiftCode: z.string(),
  shiftName: z.string(),
  shiftTiming: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  isNightShift: z.boolean().default(false),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  createdAt: instantSchema,
});
export type ShiftAssignmentDto = z.infer<typeof shiftAssignmentDtoSchema>;

// ---------------------------------------------------------------------------
// Shift Roster — date-specific entries that override the standing ShiftAssignment
// ---------------------------------------------------------------------------

export const rosterEntryStatusSchema = z.enum(['draft', 'published']);
export type RosterEntryStatus = z.infer<typeof rosterEntryStatusSchema>;

/** Which precedence tier produced the shift shown in a cell. `none` = nothing applies to that date. */
export const rosterCellSourceSchema = z.enum(['roster', 'assignment', 'none']);
export type RosterCellSource = z.infer<typeof rosterCellSourceSchema>;

/** What the day *is*, independent of which shift applies — drives the grid's non-working states.
 * `off` = an EXPLICIT RosterEntry.isOff override, distinct from `weekly_off` (the shift's own
 * recurring pattern) — see RosterEntry's own doc comment (schema.prisma) for why the two are never
 * conflated. */
export const rosterDayStateSchema = z.enum(['working', 'weekly_off', 'holiday', 'leave', 'off']);
export type RosterDayState = z.infer<typeof rosterDayStateSchema>;

/** manual = a person wrote or last touched this entry; rotation = the last write came from a
 * RotationAssignment's generator and has not been touched since — see RosterEntry.origin. Null when
 * the cell has no roster entry at all (source is 'assignment' or 'none'). */
export const rosterEntryOriginSchema = z.enum(['manual', 'rotation']);
export type RosterEntryOrigin = z.infer<typeof rosterEntryOriginSchema>;

export const workTypeOverrideSchema = z.enum(['office', 'remote', 'on_duty']);
export type WorkTypeOverride = z.infer<typeof workTypeOverrideSchema>;

export const rosterCellDtoSchema = z.object({
  date: dateOnlySchema,
  shiftId: uuidSchema.nullable(),
  shiftCode: z.string().nullable(),
  shiftName: z.string().nullable(),
  shiftTiming: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  isNightShift: z.boolean().default(false),
  /** True when the shift's window crosses midnight into the next calendar day. */
  isOvernight: z.boolean().default(false),
  source: rosterCellSourceSchema,
  /** Only set when `source === 'roster'` — an assignment-derived cell has no roster status. */
  status: rosterEntryStatusSchema.nullable(),
  entryId: uuidSchema.nullable(),
  dayState: rosterDayStateSchema,
  /** The AttendanceDay for this date is locked — the cell is read-only. */
  isLocked: z.boolean().default(false),
  conflictReason: z.string().nullable(),
  note: z.string().nullable(),
  /** True when this cell is an EXPLICIT day off (RosterEntry.isOff) — see `dayState: 'off'`. */
  isOff: z.boolean().default(false),
  origin: rosterEntryOriginSchema.nullable(),
  workType: workTypeOverrideSchema.nullable(),
});
export type RosterCellDto = z.infer<typeof rosterCellDtoSchema>;

export const rosterRowDtoSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().optional(),
  departmentName: z.string().optional(),
  locationName: z.string().optional(),
  designation: z.string().optional(),
  /** Sum of the rostered shift windows across the range, in minutes (breaks excluded). */
  scheduledMinutes: z.number().int().default(0),
  cells: z.array(rosterCellDtoSchema),
});
export type RosterRowDto = z.infer<typeof rosterRowDtoSchema>;

export const rosterConflictDtoSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  date: dateOnlySchema,
  reason: z.string(),
});
export type RosterConflictDto = z.infer<typeof rosterConflictDtoSchema>;

export const rosterSummaryDtoSchema = z.object({
  employeesScheduled: z.number().int(),
  totalScheduledMinutes: z.number().int(),
  /** Working days (not off/holiday/leave) with no shift from any source. */
  unassignedGaps: z.number().int(),
  draftCount: z.number().int(),
  conflictCount: z.number().int(),
});
export type RosterSummaryDto = z.infer<typeof rosterSummaryDtoSchema>;

export const upsertRosterEntryPayloadSchema = z
  .object({
    personId: uuidSchema,
    /** Required unless `isOff: true` — see the refine below and RosterEntry's own doc comment on
     * why an explicit day off carries no shift. */
    shiftId: uuidSchema.optional(),
    /** True = mark this date range an EXPLICIT day off, overriding the standing assignment rather
     * than falling through to it. Mutually exclusive with `shiftId`. */
    isOff: z.boolean().optional(),
    /** office | remote | on_duty | omitted (no override, in-person default). Independent of
     * `isOff`/`shiftId` — a work-type override can accompany a real shift entry (Rotating Work Type
     * Assignments' combined case) or stand alone. */
    workType: workTypeOverrideSchema.nullable().optional(),
    startDate: dateOnlySchema,
    /** Omitted assigns a single date. */
    endDate: dateOnlySchema.nullable().optional(),
    note: z.string().max(500).optional(),
  })
  .refine((data) => !data.endDate || data.endDate >= data.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  })
  .refine((data) => Boolean(data.shiftId) !== Boolean(data.isOff), {
    message: 'Set exactly one of shiftId or isOff:true',
    path: ['shiftId'],
  });
export type UpsertRosterEntryPayload = z.infer<typeof upsertRosterEntryPayloadSchema>;

/**
 * Auto-rotation: a repeating cycle of shifts laid over a date range for several employees. It only
 * ever writes DRAFT roster entries, so the existing validate → publish flow is the review step.
 */
export const applyRosterRotationPayloadSchema = z
  .object({
    personIds: z.array(uuidSchema).min(1).max(200),
    /** One entry per day of the cycle: a shift id, or null for a day with no roster entry (falls back to the assignment / weekly off). */
    cycle: z.array(uuidSchema.nullable()).min(1).max(28),
    startDate: dateOnlySchema,
    endDate: dateOnlySchema,
    /**
     * Shifts the cycle by this many days for each successive employee in `personIds`, so a team
     * rotates in staggered groups rather than everyone on the same shift on the same day.
     */
    staggerDays: z.number().int().min(0).max(27).default(0),
  })
  .refine((data) => data.endDate >= data.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  })
  .refine((data) => data.cycle.some((slot) => slot !== null), {
    message: 'The cycle needs at least one shift',
    path: ['cycle'],
  });
export type ApplyRosterRotationPayload = z.infer<typeof applyRosterRotationPayloadSchema>;

export const rosterRangePayloadSchema = z
  .object({
    startDate: dateOnlySchema,
    endDate: dateOnlySchema,
    /** Narrows the operation to one employee; omitted covers everyone in the actor's scope. */
    personId: uuidSchema.optional(),
    /**
     * The SAME filters the grid was showing. Publishing is irreversible, so it must act on exactly
     * the population the caller was looking at — without these, a grid filtered to one department
     * would publish the whole organisation while the confirmation dialog showed the smaller count.
     */
    departmentId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
    search: z.string().optional(),
  })
  .refine((data) => data.endDate >= data.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type RosterRangePayload = z.infer<typeof rosterRangePayloadSchema>;

export const attendancePunchDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  attendanceDayId: uuidSchema,
  personId: uuidSchema,
  punchType: z.enum(['in', 'out', 'break_out', 'break_in']),
  timestamp: instantSchema,
  // 'system' — the auto-clock-out sweep force-closing a forgotten OUT — is a DTO-only value, never
  // an accepted punch REQUEST value (see the punch payload schema below): a client claiming
  // deviceType 'system' would forge the audit trail's "did the employee actually punch this"
  // signal, so only the worker (writing via Prisma directly, not through this schema) can produce it.
  deviceType: z.enum(['web', 'mobile', 'biometric', 'kiosk', 'system']).default('web'),
  ipAddress: z.string().nullable().optional(),
  geoLat: z.number().nullable().optional(),
  geoLng: z.number().nullable().optional(),
  isGeoVerified: z.boolean().default(false),
  /** Reverse-geocoded from geoLat/geoLng at punch time, best-effort. Null when there were no
   * coordinates or the geocoder was unreachable — never re-resolved on read. */
  geoAddress: z.string().nullable().optional(),
  selfieUrl: z.string().nullable().optional(),
  /** True when the phone reported an accuracy worse than the company's GPS accuracy limit. Informational only. */
  lowAccuracy: z.boolean().optional(),
  isExcluded: z.boolean().default(false),
});
export type AttendancePunchDto = z.infer<typeof attendancePunchDtoSchema>;

export const dayTypeSchema = z.enum(['FD', 'HD', 'WK', 'HL', 'OD', 'LV', 'AB']);
export type DayType = z.infer<typeof dayTypeSchema>;

/** Mirrors `@hrms/attendance`'s `AnomalyCode` — a separate, hand-kept enum rather than an import,
 * since packages/shared may not depend on packages/attendance (the engine depends on shared, not
 * the other way around). `attendance-anomaly-parity.test.ts` keeps the two lists from drifting. */
export const attendanceAnomalyCodeSchema = z.enum([
  'ODD_PUNCH_COUNT',
  'DUPLICATE_PUNCH',
  'OUT_BEFORE_IN',
  'MISSING_OUT',
  'NO_SHIFT_ASSIGNED',
  'IMPLAUSIBLE_DURATION',
  'BELOW_HALF_DAY_THRESHOLD',
  'EXCESSIVE_BREAK',
  'OPEN_BREAK_AT_CLOCK_OUT',
]);
export type AttendanceAnomalyCode = z.infer<typeof attendanceAnomalyCodeSchema>;

export const attendanceAnomalySchema = z.object({
  code: attendanceAnomalyCodeSchema,
  punchIds: z.array(uuidSchema).optional(),
  detail: z.number().optional(),
});
export type AttendanceAnomaly = z.infer<typeof attendanceAnomalySchema>;

export const attendanceDayDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  employeeCode: z.string().optional(),
  designation: z.string().optional(),
  photoUrl: z.string().nullable().optional(),
  workType: z.string().nullable().optional(),
  departmentName: z.string().optional(),
  locationName: z.string().optional(),
  /**
   * Null when the person genuinely had no ShiftAssignment covering this date. Never a stand-in
   * code: a fabricated "GS (09:00 - 18:00)" reads as a real roster entry and hides the fact that
   * the engine fell back to DEFAULT_SHIFT for this day.
   */
  shiftCode: z.string().nullable().optional(),
  shiftTiming: z.string().nullable().optional(),
  /**
   * The IANA zone of the day's Location. Punch instants are UTC; rendering them in the viewer's
   * browser zone shows the wrong wall-clock time for anyone not sitting in the employee's own
   * timezone (CLAUDE.md invariant 3). Always present — AttendanceDay.locationId is non-nullable.
   */
  timezone: z.string(),
  /** True once a payroll cycle freeze has locked this day; every mutation path rejects with 412. */
  isLocked: z.boolean().default(false),
  date: dateOnlySchema,
  dayOfWeek: z.string(),
  status: z.enum(['present', 'absent', 'half_day', 'weekly_off', 'holiday', 'on_leave', 'on_duty']),
  dayType: dayTypeSchema,
  /** Only meaningful when dayType === 'OD' — true when the source OnDutyRequest was mode='remote'. */
  isRemote: z.boolean().default(false),
  workHours: z.number(),
  breakHours: z.number(),
  overtimeHours: z.number(),
  lateArrivalMinutes: z.number().int(),
  earlyExitMinutes: z.number().int(),
  /** True once earlyExitMinutes reached the resolved warning threshold — see Shift.earlyExitWarningMinutes
   * / EarlyExitPenaltyPolicy. Independent of whether a penalty also applies. */
  earlyExitWarningTriggered: z.boolean().default(false),
  /** The early-exit-ONLY component of `penaltyDays` below — NOT generally equal to it, since
   * `penaltyDays` also folds in a status-based (absence/half-day) penalty by taking the larger of
   * the two. */
  earlyExitPenaltyDaysRaw: z.number().default(0),
  penaltyDays: z.number().default(0),
  firstIn: instantSchema.nullable(),
  lastOut: instantSchema.nullable(),
  isRegularised: z.boolean().default(false),
  /** Non-blocking engine flags — excessive break, an open break at clock-out, a missing punch,
   * etc. Never prevents a punch or a status; purely informational for whoever reviews the day. */
  anomalies: z.array(attendanceAnomalySchema).default([]),
  punches: z.array(attendancePunchDtoSchema).optional(),
});
export type AttendanceDayDto = z.infer<typeof attendanceDayDtoSchema>;

export const attendanceStatsDtoSchema = z.object({
  workingDays: z.number().int(), // WD
  present: z.number().int(), // PR
  weekend: z.number().int(), // WK
  holiday: z.number().int(), // HL
  onDuty: z.number().int(), // OD
  leave: z.number().int(), // LV
  absent: z.number().int(), // AB
  halfDay: z.number().int(), // HD
  late: z.number().int(), // LT
  penalty: z.number().default(0), // PN
  /** Scheduled hours across the period — shift calendar minus weekly-offs, holidays, and full-day
   * approved leave. The same "what should have happened" definition `@hrms/capacity`'s
   * `capacityForRange` uses for the status report's own `targetHours`, so the two screens never
   * disagree about what a person's month was supposed to look like. */
  expectedHours: z.number().default(0),
});
export type AttendanceStatsDto = z.infer<typeof attendanceStatsDtoSchema>;

export const attendanceStatusDtoSchema = z.object({
  date: dateOnlySchema,
  currentTime: instantSchema,
  /** The zone `date` was resolved in, and the one `firstIn`/`lastOut` must be rendered in. */
  timezone: z.string(),
  shift: shiftDtoSchema.nullable(),
  hasPunchedInToday: z.boolean(),
  hasPunchedOutToday: z.boolean(),
  firstIn: instantSchema.nullable(),
  lastOut: instantSchema.nullable(),
  totalWorkedMinutes: z.number().int(),
  totalBreakMinutes: z.number().int().default(0),
  liveStatus: z.enum([
    'clocked_in',
    'clocked_out',
    'on_break',
    'not_started',
    'on_leave',
    'weekly_off',
    'holiday',
  ]),
  /** Today's row is frozen — the punch endpoint will reject with 412, so clients disable the action. */
  isLocked: z.boolean().default(false),
  /** Day classification independent of punch state */
  dayClassification: z.enum(['workday', 'weekly_off', 'holiday', 'on_leave']).default('workday'),
  /** Raw punch state for action gating: not_punched -> Clock In; clocked_in -> Start Break / Clock Out; on_break -> End Break / Clock Out; clocked_out -> view-only */
  punchState: z
    .enum(['not_punched', 'clocked_in', 'on_break', 'clocked_out'])
    .default('not_punched'),
  /** The currently active open IN punch event, if any */
  openPunch: attendancePunchDtoSchema.nullable().optional(),
  /** The currently active open BREAK_OUT punch event, if any */
  openBreak: attendancePunchDtoSchema.nullable().optional(),
});
export type AttendanceStatusDto = z.infer<typeof attendanceStatusDtoSchema>;

/**
 * `kind` distinguishes a normal punch correction from a training-overrun regularisation
 * (Training_Module_Plan.md §3.6/§4.4: `POST /training/assignments/:id/overrun-regularisation`
 * creates one of these with `kind = 'training_overrun'`, `requestedIn`/`requestedOut` staying null
 * because punches are never moved for an overrun, D6). Declared locally rather than imported from
 * `training.contract.ts` to avoid a circular import (that file already imports
 * `attendanceRegularisationDtoSchema` from here for its own overrun-regularisation route).
 */
export const attendanceRegularisationKindSchema = z.enum(['correction', 'training_overrun']);

/**
 * A company's regularisation rules (P1-14), stored in `Organization.settings.attendance.regularisation`. They
 * apply to a request an employee files for themselves; HR or a manager correcting someone else's day is not limited.
 * `null` turns a limit off.
 */
export const REGULARISATION_APPROVER_MODES = ['permission_holders', 'assigned_only'] as const;
export type RegularisationApproverMode = (typeof REGULARISATION_APPROVER_MODES)[number];

export const regularisationPolicySchema = z.object({
  /** Corrections per calendar month (by the date being corrected), counting pending and approved ones. */
  monthlyLimit: z.number().int().min(1).max(31).nullable(),
  /** How many days back a date may be corrected. */
  lookbackDays: z.number().int().min(1).max(365).nullable(),
  /** Whether an employee may withdraw their own pending request. */
  allowCancel: z.boolean(),
  /**
   * Who may decide a pending request. `permission_holders`: the assigned approver, plus anyone whose
   * role holds the approve permission over the person (HR). `assigned_only`: only the assigned
   * approver (the reporting manager, or the fallback approver when there is none).
   */
  approverMode: z.enum(REGULARISATION_APPROVER_MODES).default('permission_holders'),
});
export type RegularisationPolicy = z.infer<typeof regularisationPolicySchema>;

/** Used until a company sets its own: no monthly limit, up to 30 days back, cancel allowed. */
export const DEFAULT_REGULARISATION_POLICY: RegularisationPolicy = Object.freeze({
  monthlyLimit: null,
  lookbackDays: 30,
  allowCancel: true,
  approverMode: 'permission_holders',
});

export const trainingOverrunRegularisationReasonCategorySchema = z.enum([
  'meeting_overrun',
  'system_issue',
  'content_issue',
  'personal_emergency',
  'other',
]);

export const regularisationAttachmentSchema = z.object({
  id: uuidSchema,
  fileName: z.string(),
  url: z.string().nullable(),
});

export const attendanceRegularisationDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  attendanceDayId: uuidSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  date: dateOnlySchema,
  reason: z.string(),
  requestedIn: instantSchema.nullable().optional(),
  requestedOut: instantSchema.nullable().optional(),
  /** Closes a forgotten break-in — inserted as a `break_in` punch on approval, independent of
   * requestedIn/requestedOut. */
  requestedBreakIn: instantSchema.nullable().optional(),
  /** The employee's location timezone, so a reviewer reads the requested times as the employee's local times
   * rather than their own (P3-2). Present on list results. */
  timezone: z.string().optional(),
  /** `cancelled` = withdrawn by the employee while still pending (P1-14). */
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled']),
  workflowInstanceId: uuidSchema.nullable().optional(),
  remarks: z.string().nullable().optional(),
  createdAt: instantSchema,
  /** Defaults to `'correction'` on the wire via the handler, not a zod `.default()` here — every
   * existing row genuinely IS a correction, and a schema default would hide a mapper that forgets
   * to set it on a new `training_overrun` row. */
  kind: attendanceRegularisationKindSchema.default('correction'),
  trainingAssignmentId: uuidSchema.nullable().optional(),
  trainingTaskTitle: z.string().nullable().optional(),
  overrunMinutes: z.number().int().nullable().optional(),
  reasonCategory: trainingOverrunRegularisationReasonCategorySchema.nullable().optional(),
  evidenceNotes: z.string().nullable().optional(),
  attachments: z.array(regularisationAttachmentSchema).optional(),
});
export type AttendanceRegularisationDto = z.infer<typeof attendanceRegularisationDtoSchema>;

export const regularisationWorkflowStateSchema = z.object({
  instanceId: uuidSchema.nullable().optional(),
  status: z.string(),
  currentStep: z.string().nullable().optional(),
  nextStep: z.string().nullable().optional(),
  pendingApprovers: z.array(z.string()).default([]),
});
export type RegularisationWorkflowState = z.infer<typeof regularisationWorkflowStateSchema>;

export const regularisationApprovalItemSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string(),
  departmentName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  attendanceDayId: uuidSchema,
  date: dateOnlySchema,
  dateFormatted: z.string(),
  currentIn: z.string().nullable().optional(),
  currentOut: z.string().nullable().optional(),
  requestedIn: z.string().nullable().optional(),
  requestedOut: z.string().nullable().optional(),
  /** The day's currently unclosed break_out, if any — what the requested break-in (below) would
   * close. Null when there is nothing to close (the request carries no break correction). */
  currentOpenBreakOut: z.string().nullable().optional(),
  requestedBreakIn: z.string().nullable().optional(),
  adjustmentType: z.enum(['in_and_out', 'in_only', 'out_only', 'break_in_only', 'correction']),
  adjustmentTypeLabel: z.string(),
  shiftId: uuidSchema.nullable().optional(),
  shiftName: z.string(),
  shiftTiming: z.string(),
  reason: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  createdBy: z.string(),
  createdAt: instantSchema,
  createdAtFormatted: z.string(),
  actionBy: z.string().nullable().optional(),
  actionAt: instantSchema.nullable().optional(),
  actionAtFormatted: z.string().nullable().optional(),
  remarks: z.string().nullable().optional(),
  workflowState: regularisationWorkflowStateSchema,
  isLocked: z.boolean(),
  /** Training_Module_Plan.md §3.6 — same meaning as `attendanceRegularisationDtoSchema`'s fields
   * above; this approval-queue row carries its own copies because it is a denormalised read model,
   * not a wrapper around the DTO. */
  kind: attendanceRegularisationKindSchema.default('correction'),
  trainingAssignmentId: uuidSchema.nullable().optional(),
  trainingTaskTitle: z.string().nullable().optional(),
  overrunMinutes: z.number().int().nullable().optional(),
  reasonCategory: trainingOverrunRegularisationReasonCategorySchema.nullable().optional(),
  evidenceNotes: z.string().nullable().optional(),
  attachments: z.array(regularisationAttachmentSchema).optional(),
  canApprove: z.boolean(),
  attendanceDay: z.object({
    classification: z.string(),
    totalWorkHours: z.string(),
    lateMinutes: z.number().int(),
    earlyMinutes: z.number().int(),
    isLocked: z.boolean(),
  }),
});
export type RegularisationApprovalItemDto = z.infer<typeof regularisationApprovalItemSchema>;

export const regularisationApprovalSummarySchema = z.object({
  totalCount: z.number().int(),
  pendingCount: z.number().int(),
  approvedCount: z.number().int(),
  rejectedCount: z.number().int(),
});
export type RegularisationApprovalSummaryDto = z.infer<typeof regularisationApprovalSummarySchema>;

export const shiftChangeRequestDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  employeeCode: z.string().optional(),
  departmentName: z.string().optional(),
  designation: z.string().optional(),
  date: dateOnlySchema,
  /** Null for a single-day change (backward-compatible with pre-range requests). */
  endDate: dateOnlySchema.nullable().optional(),
  currentShiftId: uuidSchema.nullable().optional(),
  currentShiftLabel: z.string().nullable().optional(),
  requestedShiftId: uuidSchema,
  requestedShiftLabel: z.string().optional(),
  reason: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  workflowInstanceId: uuidSchema.nullable().optional(),
  remarks: z.string().nullable().optional(),
  actionBy: z.string().nullable().optional(),
  nextApproval: z.string().nullable().optional(),
  createdAt: instantSchema,
});
export type ShiftChangeRequestDto = z.infer<typeof shiftChangeRequestDtoSchema>;

export const onDutyRequestDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  employeeCode: z.string().optional(),
  departmentName: z.string().optional(),
  designation: z.string().optional(),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  mode: z.enum(['on_duty', 'remote']),
  reason: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  workflowInstanceId: uuidSchema.nullable().optional(),
  remarks: z.string().nullable().optional(),
  actionBy: z.string().nullable().optional(),
  nextApproval: z.string().nullable().optional(),
  lastApproval: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  daysCount: z.number().int().optional(),
  createdAt: instantSchema,
});
export type OnDutyRequestDto = z.infer<typeof onDutyRequestDtoSchema>;

export const adminRealtimeHeadcountDtoSchema = z.object({
  totalEmployees: z.number().int(),
  presentToday: z.number().int(),
  absentToday: z.number().int(),
  onLeaveToday: z.number().int(),
  lateToday: z.number().int(),
  onDutyToday: z.number().int(),
  onPremiseCount: z.number().int(),
  remoteCount: z.number().int(),
});
export type AdminRealtimeHeadcountDto = z.infer<typeof adminRealtimeHeadcountDtoSchema>;

export const earlyOutItemDtoSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  employeeCode: z.string().optional(),
  date: dateOnlySchema,
  shiftName: z.string().optional(),
  shiftEndTime: z.string().optional(),
  clockOut: z.string().nullable().optional(),
  /** The employee's location timezone; render `clockOut` in it, not in the viewer's (P3-2). */
  timezone: z.string().optional(),
  earlyExitMinutes: z.number().int(),
  /** Independent of `status` below — a day can warn without ever needing settlement (no penalty
   * reached) and can be `remain_to_settle` without ever having warned (warning not configured). */
  earlyExitWarningTriggered: z.boolean().default(false),
  /** The early-exit-ONLY penalty component — what a waiver on this day actually acts on. */
  earlyExitPenaltyDaysRaw: z.number().default(0),
  status: z.enum(['remain_to_settle', 'settled', 'extra_time']),
  reason: z.string().nullable().optional(),
  actionBy: z.string().nullable().optional(),
  isRegularised: z.boolean(),
  regularisationId: uuidSchema.nullable().optional(),
  regularisationStatus: z.string().nullable().optional(),
  /** Set once a waiver has been raised for this day, so the UI can offer "Raise Waiver" only when
   * there isn't already a live (pending/approved) one. */
  waiverId: uuidSchema.nullable().optional(),
  waiverStatus: z.enum(['pending', 'approved', 'rejected']).nullable().optional(),
});
export type EarlyOutItemDto = z.infer<typeof earlyOutItemDtoSchema>;

export const earlyOutSummaryDtoSchema = z.object({
  totalCount: z.number().int(),
  totalMinutes: z.number().int(),
  remainToSettleCount: z.number().int(),
  remainToSettleMinutes: z.number().int(),
  settledCount: z.number().int(),
  settledMinutes: z.number().int(),
  extraTimeCount: z.number().int(),
  extraTimeMinutes: z.number().int(),
});
export type EarlyOutSummaryDto = z.infer<typeof earlyOutSummaryDtoSchema>;

export const earlyOutResponseDtoSchema = z.object({
  summary: earlyOutSummaryDtoSchema,
  items: z.array(earlyOutItemDtoSchema),
});
export type EarlyOutResponseDto = z.infer<typeof earlyOutResponseDtoSchema>;

// ---------------------------------------------------------------------------
// Payloads & Queries
// ---------------------------------------------------------------------------

/**
 * A punch selfie: a small inline image (what the web app captures, roughly 100 KB) or an https link. Bounded
 * and typed so a punch can never carry an arbitrary blob into the attendance table.
 */
const DATA_IMAGE_URL = new RegExp('^data:image/(jpeg|png|webp);base64,');
const HTTPS_URL = new RegExp('^https://');

export const punchSelfieSchema = z
  .string()
  .max(1_000_000, 'Selfie is too large')
  .refine(
    (v) => (v.startsWith('data:') ? DATA_IMAGE_URL.test(v) : HTTPS_URL.test(v)),
    'Selfie must be a JPEG, PNG or WEBP image',
  );

export const punchPayloadSchema = z.object({
  punchType: z.enum(['in', 'out', 'break_out', 'break_in']),
  deviceType: z.enum(['web', 'mobile', 'biometric']).default('web'),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  /** The phone's own estimate of its position error, in metres (`GeolocationCoordinates.accuracy`). */
  accuracyM: z.number().min(0).max(1_000_000).optional(),
  selfieUrl: punchSelfieSchema.optional(),
});
export type PunchPayload = z.infer<typeof punchPayloadSchema>;

const wallClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be 24-hour HH:MM');

/**
 * `requestedInTime`/`requestedOutTime` are wall-clock local time ("09:00"), never a client-built
 * UTC instant — a client that knows only the employee's typed time and builds `${date}T${time}Z`
 * itself is silently wrong for every non-UTC location. The server resolves these against the
 * employee's actual Location.timezone (see requestRegularisation) so "09:00" always means 9am
 * where the employee actually is.
 */
export const regularisationPayloadSchema = z
  .object({
    date: dateOnlySchema,
    requestedInTime: wallClockTimeSchema.optional(),
    requestedOutTime: wallClockTimeSchema.optional(),
    /** Closes a forgotten break-in: the time the employee actually returned from break. The
     *  server refuses this unless the day genuinely has an unclosed break_out to close (see
     *  `requestRegularisation`) — never a substitute for a real break_out punch. */
    requestedBreakInTime: wallClockTimeSchema.optional(),
    reason: z.string().min(5).max(500),
    /** Omitted -> the caller's own record. Set only by a manager/admin regularising on behalf of
     *  someone in their permitted scope — the server re-checks that scope, this is never trusted
     *  as-is. */
    personId: uuidSchema.optional(),
  })
  .refine((data) => data.requestedInTime || data.requestedOutTime || data.requestedBreakInTime, {
    message: 'At least one of requestedInTime, requestedOutTime or requestedBreakInTime is required',
    path: ['requestedInTime'],
  });
export type RegularisationPayload = z.infer<typeof regularisationPayloadSchema>;

export const decideRegularisationPayloadSchema = z.object({
  action: z.enum(['approve', 'reject', 'APPROVE', 'REJECT']),
  remarks: z.string().optional(),
});
export type DecideRegularisationPayload = z.infer<typeof decideRegularisationPayloadSchema>;

// ---------------------------------------------------------------------------
// Early-Exit Penalty Policy — effective-dated grace/warning/penalty override, resolved below the
// Shift level. See EarlyExitPenaltyPolicy in schema.prisma and resolveEffectiveEarlyExitPolicy in
// @hrms/attendance for the full precedence design.
// ---------------------------------------------------------------------------

/** Exactly one of these narrows a policy to its tier — see earlyExitPenaltyPolicyPayloadSchema's
 * own refine. All null/undefined = the org-default tier. */
export const earlyExitPenaltyPolicyScopeSchema = z.object({
  legalEntityId: uuidSchema.nullable().optional(),
  gradeCode: z.string().min(1).max(64).nullable().optional(),
  jobRole: z.string().min(1).max(64).nullable().optional(),
  personId: uuidSchema.nullable().optional(),
});

export const earlyExitPenaltyPolicyDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  gradeCode: z.string().nullable(),
  jobRole: z.string().nullable(),
  personId: uuidSchema.nullable(),
  personName: z.string().optional(),
  /** The precedence tier this row occupies — derived from which scope column is set, never stored
   * separately (see the model's own doc comment). */
  tier: z.enum(['person', 'grade', 'role', 'legal_entity', 'org_default']),
  graceMinutes: z.number().int().nullable(),
  warningMinutes: z.number().int().nullable(),
  penaltyRules: earlyExitPenaltyRulesSchema.nullable(),
  monthlyCapDays: z.number().nullable(),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  isActive: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type EarlyExitPenaltyPolicyDto = z.infer<typeof earlyExitPenaltyPolicyDtoSchema>;

export const createEarlyExitPenaltyPolicyPayloadSchema = earlyExitPenaltyPolicyScopeSchema
  .extend({
    graceMinutes: z.number().int().min(0).max(120).nullable().optional(),
    warningMinutes: z.number().int().min(1).max(480).nullable().optional(),
    penaltyRules: earlyExitPenaltyRulesSchema.nullable().optional(),
    monthlyCapDays: z.number().min(0).max(31).nullable().optional(),
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema.nullable().optional(),
  })
  .refine(
    (v) =>
      [v.legalEntityId, v.gradeCode, v.jobRole, v.personId].filter((x) => x != null).length <= 1,
    {
      message:
        'Set at most one of legalEntityId, gradeCode, jobRole, personId — a policy occupies exactly one precedence tier',
      path: ['personId'],
    },
  )
  .refine((v) => !v.validTo || v.validTo >= v.validFrom, {
    message: 'validTo must be on or after validFrom',
    path: ['validTo'],
  })
  .superRefine((v, ctx) => {
    const message = warningBelowFirstTierMessage(v.warningMinutes, v.penaltyRules);
    if (message) ctx.addIssue({ code: 'custom', message, path: ['warningMinutes'] });
  });
export type CreateEarlyExitPenaltyPolicyPayload = z.infer<
  typeof createEarlyExitPenaltyPolicyPayloadSchema
>;

/** Only the mutable fields — scope (which tier a row occupies) is fixed at creation, exactly like a
 * Shift's `code`: re-scoping a policy after the fact would silently move which people it governs. */
export const updateEarlyExitPenaltyPolicyPayloadSchema = z
  .object({
    graceMinutes: z.number().int().min(0).max(120).nullable().optional(),
    warningMinutes: z.number().int().min(1).max(480).nullable().optional(),
    penaltyRules: earlyExitPenaltyRulesSchema.nullable().optional(),
    monthlyCapDays: z.number().min(0).max(31).nullable().optional(),
    validTo: dateOnlySchema.nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    const message = warningBelowFirstTierMessage(v.warningMinutes, v.penaltyRules);
    if (message) ctx.addIssue({ code: 'custom', message, path: ['warningMinutes'] });
  });
export type UpdateEarlyExitPenaltyPolicyPayload = z.infer<
  typeof updateEarlyExitPenaltyPolicyPayloadSchema
>;

// ---------------------------------------------------------------------------
// Early-Exit Waiver Request — waives only the EARLY-EXIT component of a day's penalty; see
// EarlyExitWaiverRequest in schema.prisma. Modeled directly on AttendanceRegularisation.
// ---------------------------------------------------------------------------

export const earlyExitWaiverRequestDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  employeeCode: z.string().optional(),
  attendanceDayId: uuidSchema,
  date: dateOnlySchema,
  /** The facts this waiver is about — never mutated by the waiver itself. */
  earlyExitMinutes: z.number().int(),
  earlyExitPenaltyDaysRaw: z.number(),
  reason: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  workflowInstanceId: uuidSchema.nullable().optional(),
  remarks: z.string().nullable().optional(),
  requestedBy: uuidSchema.nullable().optional(),
  decidedBy: uuidSchema.nullable().optional(),
  decidedAt: instantSchema.nullable().optional(),
  createdAt: instantSchema,
});
export type EarlyExitWaiverRequestDto = z.infer<typeof earlyExitWaiverRequestDtoSchema>;

export const requestEarlyExitWaiverPayloadSchema = z.object({
  attendanceDayId: uuidSchema,
  reason: z.string().min(5).max(500),
  /** Omitted -> the caller's own record. Same "server re-checks scope, never trusted as-is" rule as
   * regularisationPayloadSchema.personId. */
  personId: uuidSchema.optional(),
});
export type RequestEarlyExitWaiverPayload = z.infer<typeof requestEarlyExitWaiverPayloadSchema>;

export const decideEarlyExitWaiverPayloadSchema = z
  .object({
    action: z.enum(['approve', 'reject', 'APPROVE', 'REJECT']),
    remarks: z.string().optional(),
  })
  .refine((v) => /^reject$/i.test(v.action) === false || Boolean(v.remarks?.trim()), {
    message: 'A reason is required when rejecting a waiver request',
    path: ['remarks'],
  });
export type DecideEarlyExitWaiverPayload = z.infer<typeof decideEarlyExitWaiverPayloadSchema>;

// ---------------------------------------------------------------------------
// Shift Groups & Rotation — a reusable named cycle (ShiftGroup) plus one person's standing,
// effective-dated membership in it (RotationAssignment). One engine for both Shift and Work Type
// rotation — see resolveRotationSlot in @hrms/attendance and ShiftGroup's own doc comment
// (schema.prisma) for the deterministic slot formula and why `sequence` carries both dimensions.
// ---------------------------------------------------------------------------

export const rotationSlotSchema = z.object({
  shiftId: uuidSchema.nullable(),
  workType: workTypeOverrideSchema.nullable(),
});
export type RotationSlotDto = z.infer<typeof rotationSlotSchema>;

export const shiftGroupDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  sequence: z.array(rotationSlotSchema),
  cycleDays: z.number().int(),
  isActive: z.boolean(),
  /** Standing RotationAssignments currently pointing at this group — for the management list to
   * show "N employees" without a second round trip. */
  memberCount: z.number().int().optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type ShiftGroupDto = z.infer<typeof shiftGroupDtoSchema>;

export const createShiftGroupPayloadSchema = z
  .object({
    code: z.string().min(2).max(32),
    name: z.string().min(2).max(80),
    sequence: z.array(rotationSlotSchema).min(1).max(84), // 12 weeks of daily slots, a generous ceiling
    cycleDays: z.number().int().min(1).max(84),
  })
  .refine((v) => v.sequence.length === v.cycleDays, {
    message: 'sequence must have exactly cycleDays entries',
    path: ['sequence'],
  })
  .refine((v) => v.sequence.some((s) => s.shiftId !== null || s.workType !== null), {
    message:
      'At least one slot must set a shift or a work type — an all-null sequence does nothing',
    path: ['sequence'],
  });
export type CreateShiftGroupPayload = z.infer<typeof createShiftGroupPayloadSchema>;

/** `sequence`/`cycleDays` are mutable together only (same refine as create) — `code` is fixed at
 * creation, same rule as Shift's own `code`. */
export const updateShiftGroupPayloadSchema = z
  .object({
    name: z.string().min(2).max(80).optional(),
    sequence: z.array(rotationSlotSchema).min(1).max(84).optional(),
    cycleDays: z.number().int().min(1).max(84).optional(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => (v.sequence === undefined) === (v.cycleDays === undefined), {
    message: 'sequence and cycleDays must be updated together',
    path: ['sequence'],
  })
  .refine((v) => v.sequence === undefined || v.sequence.length === v.cycleDays, {
    message: 'sequence must have exactly cycleDays entries',
    path: ['sequence'],
  });
export type UpdateShiftGroupPayload = z.infer<typeof updateShiftGroupPayloadSchema>;

export const rotationAssignmentDtoSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string().optional(),
  employeeCode: z.string().optional(),
  shiftGroupId: uuidSchema,
  shiftGroupName: z.string().optional(),
  anchorDate: dateOnlySchema,
  staggerDays: z.number().int(),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  isActive: z.boolean(),
  createdAt: instantSchema,
});
export type RotationAssignmentDto = z.infer<typeof rotationAssignmentDtoSchema>;

export const createRotationAssignmentPayloadSchema = z
  .object({
    personIds: z.array(uuidSchema).min(1).max(200),
    shiftGroupId: uuidSchema,
    anchorDate: dateOnlySchema,
    /** 0 = no stagger — every listed person starts at the SAME slot on `anchorDate`. A caller
     * wanting Priya/Arjun-style staggering submits one request per distinct stagger value. */
    staggerDays: z.number().int().min(0).max(83).default(0),
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema.nullable().optional(),
  })
  .refine((v) => !v.validTo || v.validTo >= v.validFrom, {
    message: 'validTo must be on or after validFrom',
    path: ['validTo'],
  });
export type CreateRotationAssignmentPayload = z.infer<typeof createRotationAssignmentPayloadSchema>;

export const updateRotationAssignmentPayloadSchema = z.object({
  validTo: dateOnlySchema.nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateRotationAssignmentPayload = z.infer<typeof updateRotationAssignmentPayloadSchema>;

export const regenerateRotationPayloadSchema = z.object({
  /** Omitted = every active RotationAssignment in the org. Scoped to the caller's roster-manage
   * scope either way — see the route handler. */
  personIds: z.array(uuidSchema).max(200).optional(),
  /** How many days ahead to generate draft RosterEntry rows for, from today. */
  daysAhead: z.number().int().min(1).max(90).default(28),
});
export type RegenerateRotationPayload = z.infer<typeof regenerateRotationPayloadSchema>;

export const requestShiftChangePayloadSchema = z
  .object({
    personId: uuidSchema.optional(),
    date: dateOnlySchema,
    /** Omit or equal to `date` for a single-day change; a later date makes this a range. */
    endDate: dateOnlySchema.optional(),
    requestedShiftId: uuidSchema,
    reason: z.string().min(5).max(500),
  })
  .refine((data) => !data.endDate || data.endDate >= data.date, {
    message: 'endDate must be on or after date',
    path: ['endDate'],
  });
export type RequestShiftChangePayload = z.infer<typeof requestShiftChangePayloadSchema>;

export const decideShiftChangeRequestPayloadSchema = z.object({
  action: z.enum(['approve', 'reject', 'APPROVE', 'REJECT']),
  remarks: z.string().optional(),
});
export type DecideShiftChangeRequestPayload = z.infer<typeof decideShiftChangeRequestPayloadSchema>;

export const requestOnDutyPayloadSchema = z
  .object({
    personId: uuidSchema.optional(),
    startDate: dateOnlySchema,
    endDate: dateOnlySchema,
    mode: z.enum(['on_duty', 'remote']),
    reason: z.string().min(5).max(500),
    country: z.string().optional(),
    location: z.string().optional(),
  })
  .refine((data) => data.endDate >= data.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type RequestOnDutyPayload = z.infer<typeof requestOnDutyPayloadSchema>;

export const decideOnDutyRequestPayloadSchema = z.object({
  action: z.enum(['approve', 'reject', 'APPROVE', 'REJECT']),
  remarks: z.string().optional(),
});
export type DecideOnDutyRequestPayload = z.infer<typeof decideOnDutyRequestPayloadSchema>;

/** Shared by create and (the effective-value check for) update — null when there is nothing to
 * compare, since a shift with no penalty tiers has no upper bound to enforce the warning against. */
export function warningBelowFirstTierMessage(
  warningMinutes: number | null | undefined,
  penaltyRules: readonly { afterMinutes: number }[] | null | undefined,
): string | null {
  if (!warningMinutes || !penaltyRules || penaltyRules.length === 0) return null;
  const firstTier = penaltyRules[0]!.afterMinutes;
  return warningMinutes < firstTier
    ? null
    : `Warning threshold (${warningMinutes} min) must be less than the first penalty tier (${firstTier} min).`;
}

export const createShiftPayloadSchema = z
  .object({
    code: z.string().min(2).max(16),
    name: z.string().min(2).max(64),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Must be HH:MM'),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Must be HH:MM'),
    breakMinutes: z.number().int().min(0).max(180).default(60),
    breakWarningMinutes: z.number().int().min(0).max(180).nullable().optional(),
    graceMinutes: z.number().int().min(0).max(60).default(15),
    /** How many minutes before this shift's start/end to send the check-in/check-out reminder
     * (apps/worker's attendance-shift-reminder sweep). */
    reminderLeadMinutes: z.number().int().min(1).max(120).default(15),
    halfDayThresholdHours: z.number().min(1).max(12).default(4),
    fullDayThresholdHours: z.number().min(2).max(16).default(8),
    earlyExitGraceMinutes: z.number().int().min(0).max(120).default(0),
    earlyExitPenaltyRules: earlyExitPenaltyRulesSchema.nullable().optional(),
    /** Minutes early (post-grace) at which a WARNING fires. Omitted/null = no warning configured.
     * When `earlyExitPenaltyRules` is also set, must be strictly less than its first tier — see the
     * cross-field refine below. With no rules configured, any positive value is accepted: there is
     * nothing to be "less than", so the check is skipped rather than blocking the warning. */
    earlyExitWarningMinutes: z.number().int().min(1).max(480).nullable().optional(),
    /** Minutes before shift start a clock-IN is refused. Omitted/null = no restriction. */
    earlyCheckInGuardMinutes: z.number().int().min(0).max(720).nullable().optional(),
    /** Minutes past shift end at which the auto-clock-out sweep force-closes an open IN.
     * Omitted/null = disabled. */
    autoClockOutMinutes: z.number().int().min(0).max(720).nullable().optional(),
    isNightShift: z.boolean().default(false),
    weeklyOffDays: weeklyOffDaysSchema.default([0]),
    alternateOffDays: alternateOffDaysSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const message = warningBelowFirstTierMessage(
      val.earlyExitWarningMinutes,
      val.earlyExitPenaltyRules,
    );
    if (message) ctx.addIssue({ code: 'custom', message, path: ['earlyExitWarningMinutes'] });
  });
export type CreateShiftPayload = z.infer<typeof createShiftPayloadSchema>;

/**
 * All fields optional — a PATCH updates only what's supplied. `code` is deliberately excluded: it's
 * the shift's stable identifier (`@@unique([orgId, code])`) and every other identifier-shaped field
 * in this codebase (Location/Department codes) is likewise set once at creation, never renamed.
 */
export const updateShiftPayloadSchema = z.object({
  name: z.string().min(2).max(64).optional(),
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'Must be HH:MM')
    .optional(),
  endTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'Must be HH:MM')
    .optional(),
  breakMinutes: z.number().int().min(0).max(180).optional(),
  breakWarningMinutes: z.number().int().min(0).max(180).nullable().optional(),
  graceMinutes: z.number().int().min(0).max(60).optional(),
  reminderLeadMinutes: z.number().int().min(1).max(120).optional(),
  halfDayThresholdHours: z.number().min(1).max(12).optional(),
  fullDayThresholdHours: z.number().min(2).max(16).optional(),
  earlyExitGraceMinutes: z.number().int().min(0).max(120).optional(),
  earlyExitPenaltyRules: earlyExitPenaltyRulesSchema.nullable().optional(),
  /** Minutes early (post-grace) at which a WARNING fires. `null` clears it. The cross-field check
   * against `earlyExitPenaltyRules`'s first tier happens in the route handler against the EFFECTIVE
   * (existing-merged-with-incoming) values, the same way the half/full-day threshold pair is
   * checked — a partial PATCH cannot validate a cross-field rule against itself alone. */
  earlyExitWarningMinutes: z.number().int().min(1).max(480).nullable().optional(),
  /** Minutes before shift start a clock-IN is refused. `null` clears the restriction. */
  earlyCheckInGuardMinutes: z.number().int().min(0).max(720).nullable().optional(),
  /** Minutes past shift end at which the auto-clock-out sweep force-closes an open IN. `null`
   * disables it. */
  autoClockOutMinutes: z.number().int().min(0).max(720).nullable().optional(),
  isNightShift: z.boolean().optional(),
  weeklyOffDays: weeklyOffDaysSchema.optional(),
  alternateOffDays: alternateOffDaysSchema.optional(),
  isActive: z.boolean().optional(),
});
export type UpdateShiftPayload = z.infer<typeof updateShiftPayloadSchema>;

export const createShiftAssignmentPayloadSchema = z
  .object({
    personId: uuidSchema,
    shiftId: uuidSchema,
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema.nullable().optional(),
  })
  .refine((data) => !data.validTo || data.validTo >= data.validFrom, {
    message: 'validTo must be on or after validFrom',
    path: ['validTo'],
  });
export type CreateShiftAssignmentPayload = z.infer<typeof createShiftAssignmentPayloadSchema>;

/**
 * Assign ONE shift to MANY employees in a single request — the bulk counterpart of
 * `createShiftAssignment`. Deliberately partial-success, not all-or-nothing: a manager assigning a
 * shift to 100 people should not have the other 99 blocked because one has a locked day or an
 * overlapping assignment. Scope/permission failures are the one exception — see the route's own
 * summary — those refuse the whole request, since "who this may reach" is a security boundary, not
 * a per-person data conflict.
 */
export const bulkCreateShiftAssignmentPayloadSchema = z
  .object({
    personIds: z.array(uuidSchema).min(1).max(200),
    shiftId: uuidSchema,
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema.nullable().optional(),
  })
  .refine((data) => !data.validTo || data.validTo >= data.validFrom, {
    message: 'validTo must be on or after validFrom',
    path: ['validTo'],
  });
export type BulkCreateShiftAssignmentPayload = z.infer<
  typeof bulkCreateShiftAssignmentPayloadSchema
>;

export const bulkShiftAssignmentResultDtoSchema = z.object({
  assignedCount: z.number().int(),
  assigned: z.array(z.object({ personId: uuidSchema, assignmentId: uuidSchema })),
  /** Every personId that was NOT assigned, and exactly why — never silently dropped. */
  skipped: z.array(
    z.object({ personId: uuidSchema, personName: z.string().optional(), reason: z.string() }),
  ),
});
export type BulkShiftAssignmentResultDto = z.infer<typeof bulkShiftAssignmentResultDtoSchema>;

export const updateShiftAssignmentPayloadSchema = z
  .object({
    shiftId: uuidSchema.optional(),
    validFrom: dateOnlySchema.optional(),
    validTo: dateOnlySchema.nullable().optional(),
  })
  .refine((data) => !data.validFrom || !data.validTo || data.validTo >= data.validFrom, {
    message: 'validTo must be on or after validFrom',
    path: ['validTo'],
  });
export type UpdateShiftAssignmentPayload = z.infer<typeof updateShiftAssignmentPayloadSchema>;

/**
 * The period a freeze or unfreeze covers: a calendar month (`month` + `year`, as before), or any run of days
 * (`periodStart` + `periodEnd`) for a payroll cycle that is not a month, such as 26th to 25th (R-3). Exactly one of
 * the two forms must be given.
 */
const cyclePeriodFields = {
  month: z.number().int().min(1).max(12).optional(),
  year: z.number().int().min(2020).max(2050).optional(),
  periodStart: dateOnlySchema.optional(),
  periodEnd: dateOnlySchema.optional(),
};

type CyclePeriodFields = {
  month?: number | undefined;
  year?: number | undefined;
  periodStart?: string | undefined;
  periodEnd?: string | undefined;
};

/** Why a period is unusable, or null when it is a month or a sensible date range. */
export function cyclePeriodProblem(v: CyclePeriodFields): string | null {
  const hasMonth = v.month !== undefined || v.year !== undefined;
  const hasRange = v.periodStart !== undefined || v.periodEnd !== undefined;
  if (hasMonth === hasRange) return 'Give either a month and year, or a start and end date.';
  if (hasMonth) {
    return v.month !== undefined && v.year !== undefined ? null : 'Give both the month and the year.';
  }
  if (v.periodStart === undefined || v.periodEnd === undefined) return 'Give both the start and the end date.';
  if (v.periodEnd < v.periodStart) return 'The end date must be on or after the start date.';
  const days = (Date.parse(`${v.periodEnd}T00:00:00Z`) - Date.parse(`${v.periodStart}T00:00:00Z`)) / 86_400_000 + 1;
  return days > 366 ? 'A period can cover at most 366 days.' : null;
}

export const freezeCyclePayloadSchema = z
  .object({
    ...cyclePeriodFields,
    /** Omitted freezes every legal entity in the org (existing behaviour, unchanged). When set, only
     * AttendanceDay rows whose Location belongs to this legal entity are locked — CLAUDE.md invariant
     * 4: the fiscal/payroll calendar belongs to the legal entity, not the org. */
    legalEntityId: uuidSchema.optional(),
    remarks: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    const problem = cyclePeriodProblem(v);
    if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['periodStart'] });
  });
export type FreezeCyclePayload = z.infer<typeof freezeCyclePayloadSchema>;

/**
 * Reverses a freeze. Same month / legal-entity scoping as `freezeCyclePayloadSchema`, plus a
 * mandatory `reason`: unlocking a month that payroll may be waiting on is a decision someone must
 * be able to explain later, so it is recorded in the audit trail with this text.
 */
export const unfreezeCyclePayloadSchema = z
  .object({
    ...cyclePeriodFields,
    legalEntityId: uuidSchema.optional(),
    reason: z.string().trim().min(10, 'Give a reason of at least 10 characters').max(500),
  })
  .superRefine((v, ctx) => {
    const problem = cyclePeriodProblem(v);
    if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['periodStart'] });
  });
export type UnfreezeCyclePayload = z.infer<typeof unfreezeCyclePayloadSchema>;

export const statusReportDayDtoSchema = z.object({
  id: z.string(),
  date: z.string(), // "Sat 12 Sep 2026"
  rawDate: z.string(), // "2026-09-12"
  dayName: z.string(), // "Saturday"
  isWeekend: z.boolean(),
  project: z.string(),
  section: z.string(),
  activity: z.string(),
  clockIn: z.string(),
  clockOut: z.string(),
  breakTime: z.string(),
  totalHours: z.string(),
  /** Real minutes late/early, straight off AttendanceDay — never compare the display strings above. */
  lateMinutes: z.number().int().default(0),
  earlyMinutes: z.number().int().default(0),
  workHours: z.number().default(0),
  overtimeHours: z.number().default(0),
  /** The assigned shift's own duration for this date (0 on a weekend/holiday) — never a flat
   *  per-org constant, since shifts vary by person and by day. */
  targetMinutes: z.number().int().default(0),
  status: z.enum([
    'Weekend',
    'On Going',
    'Present',
    'Absent',
    'Half Day',
    'Holiday',
    'On Duty',
    'Leave',
    'On Leave',
  ]),
  exception: z.string().nullable().default(null),
  isRegularised: z.boolean().default(false),
  canRegularise: z.boolean().default(false),
  remarks: z.string(),
});
export type StatusReportDayDto = z.infer<typeof statusReportDayDtoSchema>;

export const statusReportSummaryDtoSchema = z.object({
  totalWorkingHours: z.string(),
  targetHours: z.string(),
  percentTarget: z.number(),
  avgDailyHours: z.string(),
  avgVsLastWeek: z.string(),
  presentDays: z.number().int(),
  totalCycleDays: z.number().int(),
  lateInCount: z.number().int(),
  earlyOutCount: z.number().int(),
  absentCount: z.number().int(),
  halfDayCount: z.number().int().default(0),
  leaveCount: z.number().int().default(0),
  missingOutCount: z.number().int().default(0),
  overtimeHours: z.string().default('00h 00m'),
  timeDifference: z.string().default('00h 00m'),
  onDutyCount: z.number().int(),
  weekendCount: z.number().int(),
  holidayCount: z.number().int(),
  attendanceRate: z.number(),
});
export type StatusReportSummaryDto = z.infer<typeof statusReportSummaryDtoSchema>;

export const scopedStatusReportSummarySchema = z.object({
  totalHeadcount: z.number().int(),
  presentCount: z.number().int(),
  absentCount: z.number().int(),
  halfDayCount: z.number().int(),
  leaveCount: z.number().int(),
  onDutyCount: z.number().int(),
  weeklyOffCount: z.number().int().default(0),
  lateInCount: z.number().int(),
  earlyOutCount: z.number().int(),
  missingOutCount: z.number().int(),
  /** Punches in the range whose phone reported a position error beyond the company's GPS accuracy limit. A count for
   * the dashboard only: nobody is notified. */
  lowAccuracyPunchCount: z.number().int().default(0),
  /** Punches in the range that carried coordinates but fell outside the location's geofence. Same: a count, no alert. */
  outOfFencePunchCount: z.number().int().default(0),
  totalWorkHours: z.string(),
  totalOvertimeHours: z.string(),
  avgAttendanceRate: z.number(),
  pendingExceptionsCount: z.number().int(),
});
export type ScopedStatusReportSummaryDto = z.infer<typeof scopedStatusReportSummarySchema>;

export const scopedStatusReportItemSchema = z.object({
  personId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  /** Null for a phone-only employee (no work email). */
  email: z.string().nullable(),
  photoUrl: z.string().nullable().optional(),
  departmentName: z.string(),
  locationName: z.string(),
  designation: z.string(),
  employmentStatus: z.string().optional(),
  workType: z.string().nullable().optional(),
  shiftName: z.string().optional(),
  managerName: z.string().optional(),
  presentDays: z.number().int(),
  absentDays: z.number().int(),
  halfDays: z.number().int(),
  leaveDays: z.number().int(),
  odDays: z.number().int(),
  weeklyOffDays: z.number().int().default(0),
  workHours: z.string(),
  avgHours: z.string().default('00h 00m'),
  overtimeHours: z.string(),
  lateCount: z.number().int(),
  earlyCount: z.number().int(),
  attendanceRate: z.number(),
  exceptionsCount: z.number().int(),
});
export type ScopedStatusReportItemDto = z.infer<typeof scopedStatusReportItemSchema>;

export const monthlyAttendanceDayCellSchema = z.object({
  date: z.string(),
  status: z.enum([
    'present',
    'absent',
    'half_day',
    'on_leave',
    'weekly_off',
    'holiday',
    'on_duty',
    'remote',
    'no_shift',
    'future',
  ]),
  statusCode: z.string(), // P, AB, HD, LV, OD, RM, WO, HL, -
  workHours: z.number().default(0),
  workHoursFormatted: z.string().default('00h 00m'),
  lateMinutes: z.number().int().default(0),
  earlyMinutes: z.number().int().default(0),
  firstIn: z.string().nullable().default(null),
  lastOut: z.string().nullable().default(null),
  isRegularised: z.boolean().default(false),
  isLocked: z.boolean().default(false),
  shiftName: z.string().nullable().optional(),
  shiftTiming: z.string().nullable().optional(),
  exception: z.string().nullable().optional(),
  remarks: z.string().nullable().optional(),
  leaveType: z.string().nullable().optional(),
  holidayName: z.string().nullable().optional(),
});
export type MonthlyAttendanceDayCellDto = z.infer<typeof monthlyAttendanceDayCellSchema>;

export const monthlyAttendanceCountersSchema = z.object({
  presentDays: z.number().int(),
  halfDays: z.number().int(),
  absentDays: z.number().int(),
  leaveDays: z.number().int(),
  odDays: z.number().int(),
  weeklyOffDays: z.number().int(),
  holidayDays: z.number().int(),
  lateCount: z.number().int(),
  earlyCount: z.number().int(),
  totalWorkHours: z.string(),
  avgDailyHours: z.string(),
  attendanceRate: z.number(),
});
export type MonthlyAttendanceCountersDto = z.infer<typeof monthlyAttendanceCountersSchema>;

export const monthlyAttendanceItemSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  employeeCode: z.string(),
  email: z.string().optional(),
  designation: z.string().optional(),
  departmentName: z.string().optional(),
  locationName: z.string().optional(),
  photoUrl: z.string().nullable().optional(),
  workType: z.string().nullable().optional(),
  employmentStatus: z.string(),
  shiftName: z.string().optional(),
  dailyAttendance: z.record(z.string(), monthlyAttendanceDayCellSchema),
  counters: monthlyAttendanceCountersSchema,
});
export type MonthlyAttendanceItemDto = z.infer<typeof monthlyAttendanceItemSchema>;

export const monthlyAttendanceSummarySchema = z.object({
  totalEmployees: z.number().int(),
  totalWorkingDays: z.number().int(),
  presentCount: z.number().int(),
  absentCount: z.number().int(),
  halfDayCount: z.number().int().default(0),
  leaveCount: z.number().int(),
  odCount: z.number().int(),
  weeklyOffCount: z.number().int(),
  holidayCount: z.number().int(),
});
export type MonthlyAttendanceSummaryDto = z.infer<typeof monthlyAttendanceSummarySchema>;

export const monthlyAttendanceDayHeaderSchema = z.object({
  date: z.string(),
  dayNumber: z.number().int(),
  dayOfWeek: z.string(),
  isWeekend: z.boolean(),
  isHoliday: z.boolean().default(false),
});
export type MonthlyAttendanceDayHeaderDto = z.infer<typeof monthlyAttendanceDayHeaderSchema>;

// ---------------------------------------------------------------------------
// Attendance Verification DTOs & Schemas
// ---------------------------------------------------------------------------

export const attendanceVerificationRowDtoSchema = z.object({
  id: z.string(),
  personId: uuidSchema,
  employeeName: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  employeeCode: z.string(),
  date: dateOnlySchema,
  dayStatus: z.string(),
  attendanceStatus: z.string(),
  attendanceType: z.string(),
  penalty: z.number().default(0),
  attendanceAction: z.string().nullable().optional(),
  actionBy: z.string().nullable().optional(),
  actionAt: z.string().nullable().optional(),
  actionRemarks: z.string().nullable().optional(),
  actualInTime: z.string().nullable().optional(),
  actualOutTime: z.string().nullable().optional(),
  clockIn: z.string().nullable().optional(),
  clockOut: z.string().nullable().optional(),
  totalTime: z.string().default('00h 00m'),
  totalHours: z.number().default(0),
  odRemoteWorkTime: z.string().default('00h 00m'),
  breakHours: z.string().default('00h 00m'),
  productiveHours: z.string().default('00h 00m'),
  extraTime: z.string().default('00h 00m'),
  overtimeHours: z.number().default(0),
  lateArrivalMinutes: z.number().int().default(0),
  earlyExitMinutes: z.number().int().default(0),
  earlyExitWarningTriggered: z.boolean().default(false),
  earlyExitPenaltyDaysRaw: z.number().default(0),
  shift: z.string().default('Default Shift'),
  shiftId: z.string().nullable().optional(),
  company: z.string().default(''),
  location: z.string().default(''),
  subLocation: z.string().nullable().optional(),
  jobTitle: z.string().default(''),
  department: z.string().default(''),
  country: z.string().default(''),
  state: z.string().default(''),
  clockInIp: z.string().nullable().optional(),
  clockOutIp: z.string().nullable().optional(),
  isLocked: z.boolean().default(false),
  isRegularised: z.boolean().default(false),
  isHoliday: z.boolean().default(false),
  isWeeklyOff: z.boolean().default(false),
  isLeave: z.boolean().default(false),
  isOd: z.boolean().default(false),
  isRemote: z.boolean().default(false),
  timezone: z.string().default('UTC'),
  punches: z.array(attendancePunchDtoSchema).optional(),
  activities: z
    .array(
      z.object({
        id: z.string(),
        project: z.string(),
        section: z.string(),
        activity: z.string(),
        hours: z.number(),
        remarks: z.string().nullable().optional(),
        createdAt: z.string(),
      }),
    )
    .optional(),
});
export type AttendanceVerificationRowDto = z.infer<typeof attendanceVerificationRowDtoSchema>;

export const attendanceVerificationSummaryDtoSchema = z.object({
  totalEmployees: z.number().int().default(0),
  present: z.number().int().default(0),
  absent: z.number().int().default(0),
  halfDay: z.number().int().default(0),
  leave: z.number().int().default(0),
  holiday: z.number().int().default(0),
  weeklyOff: z.number().int().default(0),
  late: z.number().int().default(0),
  earlyOut: z.number().int().default(0),
  odRemote: z.number().int().default(0),
});
export type AttendanceVerificationSummaryDto = z.infer<
  typeof attendanceVerificationSummaryDtoSchema
>;

export const recordAttendanceVerificationActionPayloadSchema = z.object({
  attendanceDayId: z.string().optional(),
  personId: uuidSchema,
  date: dateOnlySchema,
  action: z.enum(['present', 'first_half_present', 'second_half_present', 'absent']),
  remarks: z.string().optional(),
});
export type RecordAttendanceVerificationActionPayload = z.infer<
  typeof recordAttendanceVerificationActionPayloadSchema
>;

// ---------------------------------------------------------------------------
// Employee Absent / Present DTOs & Schemas
// ---------------------------------------------------------------------------

export const absentPresentStatusCategorySchema = z.enum([
  'absent',
  'shift_not_started',
  'on_leave',
  'on_break',
  'present',
  'holiday_weekend',
  'present_by_default',
  'non_working_day',
]);
export type AbsentPresentStatusCategory = z.infer<typeof absentPresentStatusCategorySchema>;

export const absentPresentRowDtoSchema = z.object({
  id: z.string(),
  personId: uuidSchema,
  employeeName: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  employeeCode: z.string(),
  jobTitle: z.string().default(''),
  department: z.string().default(''),
  location: z.string().default(''),
  subLocation: z.string().nullable().optional(),
  company: z.string().default(''),
  country: z.string().default(''),
  state: z.string().default(''),
  reportingManager: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  workPhone: z.string().nullable().optional(),
  emergencyPhone: z.string().nullable().optional(),
  emergencyContactName: z.string().nullable().optional(),
  date: dateOnlySchema,
  statusCategory: absentPresentStatusCategorySchema,
  attendanceStatus: z.string(),
  dayStatus: z.string(),
  attendanceType: z.string().default('Regular'),
  shift: z.string().default('Default Shift'),
  shiftId: z.string().nullable().optional(),
  shiftStartTime: z.string().nullable().optional(),
  shiftEndTime: z.string().nullable().optional(),
  clockIn: z.string().nullable().optional(),
  clockOut: z.string().nullable().optional(),
  totalHours: z.number().default(0),
  totalTime: z.string().default('00h 00m'),
  workedHours: z.number().default(0),
  lateArrivalMinutes: z.number().int().default(0),
  earlyExitMinutes: z.number().int().default(0),
  isLeave: z.boolean().default(false),
  leaveType: z.string().nullable().optional(),
  isHoliday: z.boolean().default(false),
  holidayName: z.string().nullable().optional(),
  isWeeklyOff: z.boolean().default(false),
  isOd: z.boolean().default(false),
  isRemote: z.boolean().default(false),
  isOnBreak: z.boolean().default(false),
  isLocked: z.boolean().default(false),
  isRegularised: z.boolean().default(false),
  punches: z.array(attendancePunchDtoSchema).optional(),
});
export type AbsentPresentRowDto = z.infer<typeof absentPresentRowDtoSchema>;

export const absentPresentSummaryDtoSchema = z.object({
  totalEmployees: z.number().int().default(0),
  absent: z.number().int().default(0),
  shiftNotStarted: z.number().int().default(0),
  onLeave: z.number().int().default(0),
  onBreak: z.number().int().default(0),
  present: z.number().int().default(0),
  holidayWeekend: z.number().int().default(0),
  presentByDefault: z.number().int().default(0),
  nonWorkingDay: z.number().int().default(0),
});
export type AbsentPresentSummaryDto = z.infer<typeof absentPresentSummaryDtoSchema>;

export const exportAttendancePdfQuerySchema = z.object({
  month: z.coerce.number().int().min(1).max(12),
  year: z.coerce.number().int(),
});

export const exportAttendancePdfResponseSchema = z.object({
  /** A `data:` URL — the whole PDF, base64-encoded, same convention as `reviewContract`'s
   * `exportReviewPdf`. Not a stored/re-downloadable file: regenerate on the next click. */
  url: z.string(),
  expiresAt: instantSchema,
});
export type ExportAttendancePdfResponse = z.infer<typeof exportAttendancePdfResponseSchema>;

// ---------------------------------------------------------------------------
// Contract Router
// ---------------------------------------------------------------------------

export const attendanceContract = c.router(
  {
    getMyAttendance: {
      method: 'GET',
      path: '/me',
      query: z.object({
        month: z.coerce.number().int().min(1).max(12),
        year: z.coerce.number().int(),
        status: z.string().optional(),
      }),
      responses: {
        200: z.object({
          days: z.array(attendanceDayDtoSchema),
          stats: attendanceStatsDtoSchema,
        }),
        401: errorBodySchema,
      },
      summary: 'Get monthly attendance ledger and 10-stat KPI counts for the caller',
      metadata: routeMeta('attendance:read'),
    },

    exportAttendancePdf: {
      method: 'GET',
      path: '/export/pdf',
      query: exportAttendancePdfQuerySchema,
      responses: {
        200: exportAttendancePdfResponseSchema,
        401: errorBodySchema,
      },
      summary: 'Server-rendered PDF of the caller\'s monthly attendance ledger and KPI summary',
      metadata: routeMeta('attendance:read'),
    },

    getMyRealtimeStatus: {
      method: 'GET',
      path: '/me/realtime',
      responses: {
        200: attendanceStatusDtoSchema,
        401: errorBodySchema,
      },
      summary: 'Get current punch status, shift timings and live timer for today',
      metadata: routeMeta('attendance:read'),
    },

    punch: {
      method: 'POST',
      path: '/punch',
      body: punchPayloadSchema,
      responses: {
        200: z.object({
          punch: attendancePunchDtoSchema,
          day: attendanceDayDtoSchema,
          status: attendanceStatusDtoSchema,
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        // Both are real, reachable outcomes of the handler's own guards: 409 for a second
        // clock-in with nothing closed in between, 412 for a clock-out with nothing open, a
        // punch against a frozen day, or — when HR made a check-in / check-out form Required —
        // assertWorkForm() refusing the punch until that form has been submitted (ADR 0009).
        // Undeclared, a client cannot type the body it must display.
        //
        // The 412's `details` (typed `unknown` on `errorBodySchema` — no new route or schema here,
        // Training_Module_Plan.md §3.6) is one of three shapes depending on which gate rejected the
        // punch, checked in this order by `assertWorkForm`/`assertTrainingClockOut` (plan §4.3):
        //   | { gate: 'work_form', ... }
        //   | { gate: 'training', code: 'TRAINING_TASKS_PENDING', blockers: {assignmentId, title,
        //       dueAt, progressPercent}[] }
        //   | { gate: 'training', code: 'TRAINING_REGULARISATION_REQUIRED', assignments:
        //       {assignmentId, title, dueAt, completedAt, overrunMinutes}[] }
        // Only the `out` punch is gated by training; `break_out` never is.
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Record clock-in or clock-out punch (Web, Mobile GPS or Biometric)',
      metadata: routeMeta('attendance:punch'),
    },

    requestRegularisation: {
      method: 'POST',
      path: '/regularisation',
      body: regularisationPayloadSchema,
      responses: {
        201: attendanceRegularisationDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Submit attendance regularisation request for missed or late punch',
      metadata: routeMeta(null),
    },

    listRegularisations: {
      method: 'GET',
      path: '/regularisation',
      query: z.object({
        scope: z.enum(['own', 'team', 'org']).default('own'),
        status: z.enum(['pending', 'approved', 'rejected', 'cancelled', 'all']).default('all'),
        // Training_Module_Plan.md §3.6: filter to one kind, e.g. the training overrun regularisation
        // form's own history view. Omitted returns both kinds, unchanged from before this field.
        kind: attendanceRegularisationKindSchema.optional(),
      }),
      responses: {
        200: z.array(attendanceRegularisationDtoSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List regularisation requests based on caller role and scope',
      metadata: routeMeta(null),
    },

    decideRegularisation: {
      method: 'POST',
      path: '/regularisation/:regularisationId/decide',
      body: decideRegularisationPayloadSchema,
      responses: {
        200: attendanceRegularisationDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Approve or reject a team member regularisation request',
      metadata: routeMeta(null),
    },

    cancelRegularisation: {
      method: 'POST',
      path: '/regularisation/:regularisationId/cancel',
      body: z.object({}).optional(),
      responses: {
        200: attendanceRegularisationDtoSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Withdraw your own pending regularisation request (P1-14)',
      metadata: routeMeta(null),
    },

    getRegularisationPolicy: {
      method: 'GET',
      path: '/settings/regularisation-policy',
      responses: {
        200: regularisationPolicySchema,
        401: errorBodySchema,
      },
      summary: "The company's regularisation limits, as the request form shows them",
      metadata: routeMeta(null),
    },

    listAttendanceRules: {
      method: 'GET',
      path: '/settings/rules',
      query: z.object({ kind: attendanceRuleKindSchema.optional() }),
      responses: { 200: z.array(attendanceRulePolicyDtoSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Every version of the company late-arrival and overtime rules (P2-2, P2-6)',
      metadata: routeMeta('attendance.policy:manage'),
    },

    saveAttendanceRule: {
      method: 'POST',
      path: '/settings/rules',
      body: saveAttendanceRulePolicyBodySchema,
      responses: {
        201: attendanceRulePolicyDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Start a new version of a late-arrival or overtime rule, from a date (today or later)',
      metadata: routeMeta('attendance.policy:manage'),
    },

    getMyLateSummary: {
      method: 'GET',
      path: '/me/late-summary',
      responses: {
        200: z.object({
          periodStart: dateOnlySchema,
          periodEnd: dateOnlySchema,
          lateDays: z.number().int(),
          lateMinutes: z.number().int(),
          /** What the company's rule would deduct for this month so far. */
          computedDays: z.number(),
          /** What it deducts from pay (0 when the rule only reports). */
          penaltyDays: z.number(),
          rule: z
            .object({
              basis: z.enum(['count', 'minutes']),
              freeLates: z.number().int(),
              everyNLates: z.number().int(),
              freeMinutes: z.number().int(),
              perMinutes: z.number().int(),
              deductDays: z.number(),
              action: z.enum(['lop', 'none']),
            })
            .nullable(),
        }),
        401: errorBodySchema,
      },
      summary: "The caller's late arrivals this month and what the company's late rule makes of them",
      metadata: routeMeta('attendance:read'),
    },

    listOvertimeApprovals: {
      method: 'GET',
      path: '/overtime',
      query: z.object({ status: z.enum(['pending', 'approved', 'rejected']).default('pending') }),
      responses: {
        200: z.array(
          z.object({
            attendanceDayId: uuidSchema,
            personId: uuidSchema,
            personName: z.string(),
            date: dateOnlySchema,
            overtimeHours: z.number(),
            status: z.enum(['pending', 'approved', 'rejected']),
          }),
        ),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Overtime days waiting for (or given) a decision, for the people the caller approves (P2-6)',
      metadata: routeMeta('overtime:approve'),
    },

    decideOvertime: {
      method: 'POST',
      path: '/overtime/:attendanceDayId/decide',
      body: z.object({ action: z.enum(['approve', 'reject']) }),
      responses: {
        200: z.object({ attendanceDayId: uuidSchema, status: z.enum(['approved', 'rejected']) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: "Approve or reject a day's overtime (P2-6)",
      metadata: routeMeta('overtime:approve'),
    },

    updateRegularisationPolicy: {
      method: 'PUT',
      path: '/settings/regularisation-policy',
      body: regularisationPolicySchema,
      responses: {
        200: regularisationPolicySchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "Set the company's regularisation limits",
      metadata: routeMeta('attendance.policy:manage'),
    },

    // -- Early-Exit Penalty Policy (CRUD) --------------------------------------------------------

    createEarlyExitPenaltyPolicy: {
      method: 'POST',
      path: '/early-exit-policies',
      body: createEarlyExitPenaltyPolicyPayloadSchema,
      responses: {
        201: earlyExitPenaltyPolicyDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create an effective-dated early-exit grace/warning/penalty policy override',
      metadata: routeMeta('attendance.penalty:manage'),
    },

    listEarlyExitPenaltyPolicies: {
      method: 'GET',
      path: '/early-exit-policies',
      query: z.object({
        includeInactive: z.enum(['true', 'false']).default('false'),
      }),
      responses: {
        200: z.array(earlyExitPenaltyPolicyDtoSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List early-exit penalty policies for the org',
      metadata: routeMeta('attendance.penalty:manage'),
    },

    updateEarlyExitPenaltyPolicy: {
      method: 'PATCH',
      path: '/early-exit-policies/:policyId',
      body: updateEarlyExitPenaltyPolicyPayloadSchema,
      responses: {
        200: earlyExitPenaltyPolicyDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update an early-exit penalty policy (never its scope/tier)',
      metadata: routeMeta('attendance.penalty:manage'),
    },

    // -- Early-Exit Waiver Request ----------------------------------------------------------------

    requestEarlyExitWaiver: {
      method: 'POST',
      path: '/early-exit-waivers',
      body: requestEarlyExitWaiverPayloadSchema,
      responses: {
        201: earlyExitWaiverRequestDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Request a waiver of a day’s early-exit penalty component',
      metadata: routeMeta(null),
    },

    listEarlyExitWaiverRequests: {
      method: 'GET',
      path: '/early-exit-waivers',
      query: z.object({
        scope: z.enum(['own', 'team', 'org']).default('own'),
        status: z.enum(['pending', 'approved', 'rejected', 'all']).default('all'),
      }),
      responses: {
        200: z.array(earlyExitWaiverRequestDtoSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List early-exit waiver requests based on caller role and scope',
      metadata: routeMeta(null),
    },

    decideEarlyExitWaiverRequest: {
      method: 'POST',
      path: '/early-exit-waivers/:waiverId/decide',
      body: decideEarlyExitWaiverPayloadSchema,
      responses: {
        200: earlyExitWaiverRequestDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve or reject a team member’s early-exit waiver request',
      metadata: routeMeta(null),
    },

    // -- Shift Groups (CRUD) ----------------------------------------------------------------------

    createShiftGroup: {
      method: 'POST',
      path: '/shift-groups',
      body: createShiftGroupPayloadSchema,
      responses: {
        201: shiftGroupDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a reusable named rotation cycle (shift and/or work type per slot)',
      metadata: routeMeta('shift.rotation:manage'),
    },

    listShiftGroups: {
      method: 'GET',
      path: '/shift-groups',
      query: z.object({ includeInactive: z.enum(['true', 'false']).default('false') }),
      responses: {
        200: z.array(shiftGroupDtoSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      // Read-only listing uses shift:read (held by every employee), same split as getShifts vs
      // shift:manage — populating a rotation-assignment dropdown is not itself a management action.
      // Mutating routes below stay on shift.rotation:manage.
      summary: 'List shift groups for the org',
      metadata: routeMeta('shift:read'),
    },

    updateShiftGroup: {
      method: 'PATCH',
      path: '/shift-groups/:shiftGroupId',
      body: updateShiftGroupPayloadSchema,
      responses: {
        200: shiftGroupDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update a shift group’s sequence, name, or active state',
      metadata: routeMeta('shift.rotation:manage'),
    },

    // -- Rotating Shift / Work Type Assignments ----------------------------------------------------

    createRotationAssignment: {
      method: 'POST',
      path: '/rotation-assignments',
      body: createRotationAssignmentPayloadSchema,
      responses: {
        201: z.object({
          assignedCount: z.number().int(),
          assigned: z.array(rotationAssignmentDtoSchema),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Put one or more employees onto a shift group’s standing rotation',
      metadata: routeMeta('shift.rotation:manage'),
    },

    listRotationAssignments: {
      method: 'GET',
      path: '/rotation-assignments',
      query: z.object({
        personId: uuidSchema.optional(),
        shiftGroupId: uuidSchema.optional(),
        includeInactive: z.enum(['true', 'false']).default('false'),
      }),
      responses: {
        200: z.array(rotationAssignmentDtoSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List standing rotation assignments, scoped to the caller’s roster-manage reach',
      // The real permission check is inside resolveRosterPersonScope (throws if the actor holds
      // shift.rotation:manage in NO scope at all) — indirect, so declared null here, same
      // convention listRegularisations/listOnDutyRequests already use for the identical shape.
      metadata: routeMeta(null),
    },

    updateRotationAssignment: {
      method: 'PATCH',
      path: '/rotation-assignments/:assignmentId',
      body: updateRotationAssignmentPayloadSchema,
      responses: {
        200: rotationAssignmentDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'End-date or deactivate a standing rotation assignment',
      metadata: routeMeta('shift.rotation:manage'),
    },

    regenerateRotationDrafts: {
      method: 'POST',
      path: '/rotation-assignments/regenerate',
      body: regenerateRotationPayloadSchema,
      responses: {
        200: z.object({
          assignmentCount: z.number().int(),
          entryCount: z.number().int(),
          skippedCount: z.number().int(),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Run the rotation generator now (same logic the nightly worker job runs) and stage draft RosterEntry rows',
      metadata: routeMeta('shift.rotation:manage'),
    },

    requestShiftChange: {
      method: 'POST',
      path: '/shift-change',
      body: requestShiftChangePayloadSchema,
      responses: {
        201: shiftChangeRequestDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      summary: 'Request a one-off shift change for a specific date',
      metadata: routeMeta('shift.change:request'),
    },

    listShiftChangeRequests: {
      method: 'GET',
      path: '/shift-change',
      query: z.object({
        scope: z.enum(['own', 'team', 'org']).default('own'),
        status: z.enum(['pending', 'approved', 'rejected', 'all']).default('all'),
      }),
      responses: {
        200: z.array(shiftChangeRequestDtoSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List shift change requests based on caller role and scope',
      metadata: routeMeta(null),
    },

    decideShiftChangeRequest: {
      method: 'POST',
      path: '/shift-change/:shiftChangeRequestId/decide',
      body: decideShiftChangeRequestPayloadSchema,
      responses: {
        200: shiftChangeRequestDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Approve or reject a team member shift change request',
      metadata: routeMeta(null),
    },

    requestOnDuty: {
      method: 'POST',
      path: '/on-duty',
      body: requestOnDutyPayloadSchema,
      responses: {
        201: onDutyRequestDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
      },
      summary: 'Request on-duty or remote-work status over a date range',
      metadata: routeMeta(null),
    },

    listOnDutyRequests: {
      method: 'GET',
      path: '/on-duty',
      query: z.object({
        scope: z.enum(['own', 'team', 'org']).default('own'),
        status: z.enum(['pending', 'approved', 'rejected', 'all']).default('all'),
        mode: z.enum(['on_duty', 'remote', 'all']).optional(),
        startDate: dateOnlySchema.optional(),
        endDate: dateOnlySchema.optional(),
        search: z.string().optional(),
        personId: uuidSchema.optional(),
        departmentId: uuidSchema.optional(),
        locationId: uuidSchema.optional(),
      }),
      responses: {
        200: z.array(onDutyRequestDtoSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List on-duty/remote-work requests based on caller role and scope',
      metadata: routeMeta(null),
    },

    decideOnDutyRequest: {
      method: 'POST',
      path: '/on-duty/:onDutyRequestId/decide',
      body: decideOnDutyRequestPayloadSchema,
      responses: {
        200: onDutyRequestDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Approve or reject a team member on-duty/remote-work request',
      metadata: routeMeta(null),
    },

    getAdminAttendanceRecords: {
      method: 'GET',
      path: '/admin/records',
      query: paginationQuerySchema.extend({
        date: dateOnlySchema.optional(),
        startDate: dateOnlySchema.optional(),
        endDate: dateOnlySchema.optional(),
        month: z.coerce.number().int().optional(),
        year: z.coerce.number().int().optional(),
        departmentId: uuidSchema.optional(),
        locationId: uuidSchema.optional(),
        shiftId: uuidSchema.optional(),
        status: z.string().optional(),
        search: z.string().optional(),
        scope: z.enum(['team', 'org']).optional(),
        personId: uuidSchema.optional(),
        dayType: z.string().optional(),
        employmentStatus: z.string().optional(),
        workType: z.string().optional(),
        page: z.coerce.number().int().min(1).optional(),
      }),
      responses: {
        200: paginatedSchema(attendanceDayDtoSchema).extend({
          total: z.number().int().optional(),
          page: z.number().int().optional(),
          limit: z.number().int().optional(),
          summary: z
            .object({
              totalCount: z.number().int(),
              presentCount: z.number().int(),
              halfDayCount: z.number().int().default(0),
              absentCount: z.number().int(),
              lateCount: z.number().int(),
              earlyCount: z.number().int(),
              leaveCount: z.number().int(),
              odCount: z.number().int(),
              weeklyOffCount: z.number().int().default(0),
              holidayCount: z.number().int().default(0),
            })
            .optional(),
          departments: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          locations: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          shifts: z
            .array(
              z.object({
                id: z.string(),
                code: z.string(),
                name: z.string(),
                startTime: z.string(),
                endTime: z.string(),
              }),
            )
            .optional(),
          employees: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
                employeeCode: z.string().optional(),
              }),
            )
            .optional(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Get organization attendance records across employees with filters and summary KPI counts',
      metadata: routeMeta(null),
    },

    getAdminRealtime: {
      method: 'GET',
      path: '/admin/realtime',
      responses: {
        200: adminRealtimeHeadcountDtoSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Real-time headcount status (Present, Absent, On Leave, Late) for HR/Admin',
      metadata: routeMeta('attendance.team:read'),
    },

    getShifts: {
      method: 'GET',
      path: '/shifts',
      responses: {
        200: z.array(shiftDtoSchema),
        401: errorBodySchema,
      },
      summary: 'List configured shifts for the organization',
      metadata: routeMeta('shift:read'),
    },

    getEffectiveShift: {
      method: 'GET',
      path: '/shifts/effective',
      query: z.object({
        personId: uuidSchema.optional(),
        date: dateOnlySchema,
        endDate: dateOnlySchema.optional(),
      }),
      responses: {
        200: z.object({
          personId: z.string(),
          personName: z.string().optional(),
          employeeCode: z.string().optional(),
          departmentName: z.string().optional(),
          designation: z.string().optional(),
          locationName: z.string().optional(),
          shifts: z.array(
            z.object({
              date: z.string(),
              dayOfWeek: z.string().optional(),
              shiftId: z.string().nullable(),
              shiftCode: z.string().nullable(),
              shiftName: z.string().nullable(),
              shiftTiming: z.string().nullable(),
              startTime: z.string().nullable(),
              endTime: z.string().nullable(),
              isWeeklyOff: z.boolean().default(false),
              /** True when a published RosterEntry marks this date an EXPLICIT day off — see
               * RosterEntry.isOff. Distinct from `isWeeklyOff`, the shift's own recurring pattern. */
              isExplicitOff: z.boolean().default(false),
            }),
          ),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Get effective shift details for an employee on a specific date or date range',
    },

    listShifts: {
      method: 'GET',
      path: '/shifts/manage',
      query: z.object({
        /** Narrows to one shift — how the edit form loads a shift that may be inactive, since
         * `getShifts` deliberately only returns active ones. */
        shiftId: uuidSchema.optional(),
        search: z.string().optional(),
        status: z.enum(['all', 'active', 'inactive']).default('all'),
        crossMidnight: z.enum(['all', 'yes', 'no']).default('all'),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(20),
      }),
      responses: {
        200: z.object({
          items: z.array(shiftManagementRowDtoSchema),
          total: z.number().int(),
          page: z.number().int(),
          limit: z.number().int(),
          summary: shiftManagementSummaryDtoSchema,
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      /** Distinct from `getShifts`, which is the ACTIVE-only picker six screens depend on. This one
       * is the admin catalogue: it includes inactive shifts, filters, pagination and counts. */
      summary:
        'Shift catalogue for the management screen — includes inactive shifts, filters and counts',
      metadata: routeMeta('shift:manage'),
    },

    createShift: {
      method: 'POST',
      path: '/shifts',
      body: createShiftPayloadSchema,
      responses: {
        201: shiftDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Create a new shift policy',
      metadata: routeMeta('shift:manage'),
    },

    updateShift: {
      method: 'PATCH',
      path: '/shifts/:shiftId',
      body: updateShiftPayloadSchema,
      responses: {
        200: shiftDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Update a shift policy — never retroactively recalculates historical AttendanceDay records',
      metadata: routeMeta('shift:manage'),
    },

    listShiftAssignments: {
      method: 'GET',
      path: '/shift-assignments',
      query: paginationQuerySchema.extend({
        personId: uuidSchema.optional(),
        shiftId: uuidSchema.optional(),
        departmentId: uuidSchema.optional(),
        locationId: uuidSchema.optional(),
        scope: z.enum(['team', 'org']).optional(),
        status: z.enum(['active', 'upcoming', 'past', 'all']).default('all'),
        search: z.string().optional(),
        startDate: dateOnlySchema.optional(),
        endDate: dateOnlySchema.optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(20),
      }),
      responses: {
        200: z.object({
          items: z.array(shiftAssignmentDtoSchema),
          total: z.number().int(),
          page: z.number().int(),
          limit: z.number().int(),
          departments: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          locations: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          shifts: z.array(shiftDtoSchema).optional(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta([
        'shift:manage',
        'attendance:update',
        'shift.roster:manage',
        'shift.change:approve',
        'attendance:approve',
      ]),
      summary: 'List employee shift assignments with server-side filters and scope enforcement',
    },

    createShiftAssignment: {
      method: 'POST',
      path: '/shift-assignments',
      body: createShiftAssignmentPayloadSchema,
      responses: {
        201: shiftAssignmentDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary:
        'Assign a shift to an employee with date range validation, overlap checks, and payroll lock protection',
    },

    bulkCreateShiftAssignment: {
      method: 'POST',
      path: '/shift-assignments/bulk',
      body: bulkCreateShiftAssignmentPayloadSchema,
      responses: {
        201: bulkShiftAssignmentResultDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary:
        "Assign one shift to many employees at once — refuses the whole request if any target is outside the caller's scope; per-person data conflicts (locked day, overlap) are skipped and reported instead of blocking the rest",
    },

    updateShiftAssignment: {
      method: 'PATCH',
      path: '/shift-assignments/:assignmentId',
      body: updateShiftAssignmentPayloadSchema,
      responses: {
        200: shiftAssignmentDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary:
        'Update an existing shift assignment with effective date validation and payroll lock protection',
    },

    deleteShiftAssignment: {
      method: 'DELETE',
      path: '/shift-assignments/:assignmentId',
      body: c.type<undefined>(),
      responses: {
        200: z.object({ success: z.boolean(), message: z.string() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary:
        'Delete or end a future shift assignment without altering locked historical attendance',
    },

    getRoster: {
      method: 'GET',
      path: '/roster',
      query: z.object({
        startDate: dateOnlySchema,
        endDate: dateOnlySchema,
        departmentId: uuidSchema.optional(),
        locationId: uuidSchema.optional(),
        shiftId: uuidSchema.optional(),
        search: z.string().optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(25),
      }),
      responses: {
        200: z.object({
          startDate: dateOnlySchema,
          endDate: dateOnlySchema,
          dates: z.array(dateOnlySchema),
          rows: z.array(rosterRowDtoSchema),
          total: z.number().int(),
          page: z.number().int(),
          limit: z.number().int(),
          summary: rosterSummaryDtoSchema,
          conflicts: z.array(rosterConflictDtoSchema),
          departments: z.array(z.object({ id: z.string(), name: z.string() })),
          locations: z.array(z.object({ id: z.string(), name: z.string() })),
          shifts: z.array(shiftDtoSchema),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Roster grid for a date range — draft + published, scope-enforced and paginated by employee',
      metadata: routeMeta([
        'shift:manage',
        'attendance:update',
        'shift.roster:manage',
        'shift.change:approve',
        'attendance:approve',
      ]),
    },

    getMyRoster: {
      method: 'GET',
      path: '/roster/my',
      query: z.object({
        startDate: dateOnlySchema,
        endDate: dateOnlySchema,
      }),
      responses: {
        200: z.object({
          personId: uuidSchema,
          startDate: dateOnlySchema,
          endDate: dateOnlySchema,
          cells: z.array(rosterCellDtoSchema),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: "The caller's own PUBLISHED roster — drafts are never visible to an employee",
    },

    /**
     * ATT-10: the statutory muster-roll / compliance register — one row per employee, one column
     * per calendar day of the month, the day's ACTUAL outcome (`AttendanceDay.dayType`), not the
     * roster's scheduled shift (`getRoster`/`getMyRoster` above answer "who is scheduled to work
     * when"; this answers "what actually happened", which is what a statutory register records).
     * No new model: every field already exists on `AttendanceDay`, this is purely a reshaping query.
     */
    getMusterRoll: {
      method: 'GET',
      path: '/muster-roll',
      query: z.object({
        legalEntityId: uuidSchema,
        /** "YYYY-MM". */
        month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Must be YYYY-MM'),
        departmentId: uuidSchema.optional(),
      }),
      responses: {
        200: z.object({
          month: z.string(),
          dates: z.array(dateOnlySchema),
          rows: z.array(
            z.object({
              personId: uuidSchema,
              personName: z.string(),
              employeeCode: z.string().nullable(),
              departmentName: z.string().nullable(),
              /** Keyed by date — a date with no `AttendanceDay` row yet (a future date within the
               * current month) is simply absent from this map, not a fabricated entry. */
              days: z.record(
                z.string(),
                z.object({
                  dayType: z.enum(['FD', 'HD', 'WK', 'HL', 'OD', 'LV', 'AB']),
                  workHours: z.number(),
                }),
              ),
              summary: z.object({
                present: z.number().int(),
                absent: z.number().int(),
                onLeave: z.number().int(),
                holiday: z.number().int(),
                weeklyOff: z.number().int(),
                onDuty: z.number().int(),
              }),
            }),
          ),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "One legal entity's statutory muster roll for one calendar month",
      metadata: routeMeta('attendance.team:read'),
    },

    upsertRosterEntry: {
      method: 'POST',
      path: '/roster/entries',
      body: upsertRosterEntryPayloadSchema,
      responses: {
        201: z.object({
          entries: z.array(
            z.object({
              id: uuidSchema,
              personId: uuidSchema,
              date: dateOnlySchema,
              shiftId: uuidSchema.nullable(),
              isOff: z.boolean().default(false),
              status: rosterEntryStatusSchema,
            }),
          ),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary:
        'Draft a date-specific roster entry (or range) for an employee, with conflict and lock validation',
    },

    applyRosterRotation: {
      method: 'POST',
      path: '/roster/rotation',
      body: applyRosterRotationPayloadSchema,
      responses: {
        201: z.object({
          personCount: z.number().int(),
          entryCount: z.number().int(),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary:
        'Lay a repeating shift cycle over a date range for several employees as DRAFT roster entries',
    },

    deleteRosterEntry: {
      method: 'DELETE',
      path: '/roster/entries/:entryId',
      body: c.type<undefined>(),
      responses: {
        200: z.object({ success: z.boolean(), message: z.string() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary: 'Remove a roster entry, reverting the date to its standing shift assignment',
    },

    validateRoster: {
      method: 'POST',
      path: '/roster/validate',
      body: rosterRangePayloadSchema,
      responses: {
        200: z.object({
          valid: z.boolean(),
          draftCount: z.number().int(),
          conflicts: z.array(rosterConflictDtoSchema),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary: 'Dry-run validation of the draft roster in a range — never mutates',
    },

    publishRoster: {
      method: 'POST',
      path: '/roster/publish',
      body: rosterRangePayloadSchema,
      responses: {
        200: z.object({
          publishedCount: z.number().int(),
          recalculatedCount: z.number().int(),
          message: z.string(),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['shift:manage', 'attendance:update', 'shift.roster:manage']),
      summary:
        'Validate then publish every draft entry in range — publishing is what makes a roster resolve',
    },

    freezeCycle: {
      method: 'POST',
      path: '/admin/freeze-cycle',
      body: freezeCyclePayloadSchema,
      responses: {
        200: z.object({
          success: z.boolean(),
          frozenCount: z.number().int(),
          message: z.string(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Lock attendance cycle for payroll calculation cutoff',
      metadata: routeMeta('attendance.cycle:freeze'),
    },

    unfreezeCycle: {
      method: 'POST',
      path: '/admin/unfreeze-cycle',
      body: unfreezeCyclePayloadSchema,
      responses: {
        200: z.object({
          success: z.boolean(),
          unlockedCount: z.number().int(),
          message: z.string(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Unlock a frozen attendance cycle. Refused while a payroll run whose period overlaps the month has already used it; the reason is recorded in the audit trail.',
      metadata: routeMeta('attendance.cycle:freeze'),
    },

    getMyStatusReport: {
      method: 'GET',
      path: '/status-report',
      query: z.object({
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        personId: uuidSchema.optional(),
      }),
      responses: {
        200: z.object({
          summary: statusReportSummaryDtoSchema,
          days: z.array(statusReportDayDtoSchema),
          shiftInfo: z.object({
            name: z.string(),
            timing: z.string(),
            gracePeriod: z.string(),
            workingDays: z.string(),
            weekoff: z.string(),
          }),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Get status report for caller or authorized target employee with KPI summary and daily records',
      metadata: routeMeta(null),
    },

    getScopedStatusReport: {
      method: 'GET',
      path: '/status-report/scoped',
      query: z.object({
        scope: z.enum(['team', 'org']).optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        personId: uuidSchema.optional(),
        departmentId: uuidSchema.optional(),
        locationId: uuidSchema.optional(),
        shiftId: uuidSchema.optional(),
        employmentStatus: z.string().optional(),
        workType: z.string().optional(),
        designation: z.string().optional(),
        status: z.string().optional(),
        search: z.string().optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(20),
      }),
      responses: {
        200: z.object({
          summary: scopedStatusReportSummarySchema,
          items: z.array(scopedStatusReportItemSchema),
          total: z.number().int(),
          page: z.number().int(),
          limit: z.number().int(),
          departments: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          locations: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          shifts: z
            .array(
              z.object({
                id: z.string(),
                code: z.string(),
                name: z.string(),
                startTime: z.string(),
                endTime: z.string(),
              }),
            )
            .optional(),
          employees: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
                employeeCode: z.string().optional(),
              }),
            )
            .optional(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Get aggregated team or organization status report without N+1 queries',
      metadata: routeMeta(null),
    },

    getEarlyOutRecords: {
      method: 'GET',
      path: '/early-out',
      query: z.object({
        scope: z.enum(['own', 'team', 'org']).default('own'),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        status: z.enum(['all', 'remain_to_settle', 'settled', 'extra_time']).default('all'),
        search: z.string().optional(),
      }),
      responses: {
        200: earlyOutResponseDtoSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Get early departure records and summary metrics with real server calculations',
      metadata: routeMeta(null),
    },

    getMonthlyAttendance: {
      method: 'GET',
      path: '/monthly',
      query: z.object({
        scope: z.enum(['team', 'org']).optional(),
        month: z.string().optional(), // "YYYY-MM"
        startDate: z.string().optional(), // "YYYY-MM-DD"
        endDate: z.string().optional(), // "YYYY-MM-DD"
        personId: uuidSchema.optional(),
        departmentId: uuidSchema.optional(),
        locationId: uuidSchema.optional(),
        shiftId: uuidSchema.optional(),
        employmentStatus: z.string().optional(),
        workType: z.string().optional(),
        designation: z.string().optional(),
        search: z.string().optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(10),
      }),
      responses: {
        200: z.object({
          summary: monthlyAttendanceSummarySchema,
          daysInMonth: z.array(monthlyAttendanceDayHeaderSchema),
          items: z.array(monthlyAttendanceItemSchema),
          total: z.number().int(),
          page: z.number().int(),
          limit: z.number().int(),
          departments: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          locations: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          shifts: z
            .array(
              z.object({
                id: z.string(),
                code: z.string(),
                name: z.string(),
                startTime: z.string(),
                endTime: z.string(),
              }),
            )
            .optional(),
          employees: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
                employeeCode: z.string().optional(),
              }),
            )
            .optional(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Get monthly attendance matrix and day-by-day status cells without N+1 queries',
      metadata: routeMeta(null),
    },

    getRegularisationApprovals: {
      method: 'GET',
      path: '/regularisations',
      query: z.object({
        scope: z.enum(['team', 'org', 'all']).optional(),
        status: z.enum(['all', 'pending', 'approved', 'rejected']).default('all'),
        // Training_Module_Plan.md §3.6: lets the approvals queue split "attendance corrections" from
        // "training overruns" into separate tabs/filters instead of one mixed list.
        kind: attendanceRegularisationKindSchema.optional(),
        personId: uuidSchema.optional(),
        departmentId: uuidSchema.optional(),
        locationId: uuidSchema.optional(),
        shiftId: uuidSchema.optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        search: z.string().optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(10),
        sortBy: z.enum(['date', 'createdAt', 'name']).default('createdAt'),
        sortOrder: z.enum(['asc', 'desc']).default('desc'),
      }),
      responses: {
        200: z.object({
          items: z.array(regularisationApprovalItemSchema),
          summary: regularisationApprovalSummarySchema,
          total: z.number().int(),
          page: z.number().int(),
          limit: z.number().int(),
          departments: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          locations: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          shifts: z
            .array(
              z.object({
                id: z.string(),
                code: z.string(),
                name: z.string(),
                startTime: z.string(),
                endTime: z.string(),
              }),
            )
            .optional(),
          employees: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
                employeeCode: z.string().optional(),
              }),
            )
            .optional(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List attendance regularisation requests with full approval matrix metadata',
      metadata: routeMeta(null),
    },

    getRegularisationApprovalById: {
      method: 'GET',
      path: '/regularisations/:requestId',
      responses: {
        200: regularisationApprovalItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get details of a single attendance regularisation request for review',
      metadata: routeMeta(null),
    },

    getAttendanceVerification: {
      method: 'GET',
      path: '/verification',
      query: z.object({
        date: z.string().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        search: z.string().optional(),
        employeeId: z.string().optional(),
        status: z.string().optional(),
        attendanceStatus: z.string().optional(),
        attendanceType: z.string().optional(),
        clockType: z.string().optional(),
        clockInMethod: z.string().optional(),
        shiftId: z.string().optional(),
        departmentId: z.string().optional(),
        locationId: z.string().optional(),
        company: z.string().optional(),
        subLocation: z.string().optional(),
        jobTitle: z.string().optional(),
        employmentStatus: z.string().optional(),
        country: z.string().optional(),
        state: z.string().optional(),
        gender: z.string().optional(),
        scope: z.enum(['team', 'org', 'all']).optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(200).default(20),
        export: z.enum(['csv']).optional(),
      }),
      responses: {
        200: z.object({
          rows: z.array(attendanceVerificationRowDtoSchema),
          summary: attendanceVerificationSummaryDtoSchema,
          total: z.number().int(),
          page: z.number().int(),
          pageSize: z.number().int(),
          departments: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          locations: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          shifts: z
            .array(
              z.object({
                id: z.string(),
                code: z.string(),
                name: z.string(),
                startTime: z.string(),
                endTime: z.string(),
              }),
            )
            .optional(),
          employees: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
                employeeCode: z.string().optional(),
              }),
            )
            .optional(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Get attendance verification table data and summary KPIs with server-side scoping and filtering',
      metadata: routeMeta(null),
    },

    recordAttendanceVerificationAction: {
      method: 'POST',
      path: '/verification/action',
      body: recordAttendanceVerificationActionPayloadSchema,
      responses: {
        200: attendanceDayDtoSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'Record manual attendance verification action (Present, First Half Present, Second Half Present, Absent)',
      metadata: routeMeta(null),
    },

    getAbsentPresent: {
      method: 'GET',
      path: '/absent-present',
      query: z.object({
        date: dateOnlySchema.optional(),
        search: z.string().optional(),
        statusCategory: z.string().optional(),
        attendanceStatus: z.string().optional(),
        dayStatus: z.string().optional(),
        shiftId: z.string().optional(),
        departmentId: z.string().optional(),
        locationId: z.string().optional(),
        company: z.string().optional(),
        subLocation: z.string().optional(),
        jobTitle: z.string().optional(),
        country: z.string().optional(),
        state: z.string().optional(),
        gender: z.string().optional(),
        scope: z.enum(['team', 'org', 'all']).optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(20),
        export: z.enum(['csv']).optional(),
      }),
      responses: {
        200: z.object({
          rows: z.array(absentPresentRowDtoSchema),
          total: z.number().int(),
          page: z.number().int(),
          pageSize: z.number().int(),
          summary: absentPresentSummaryDtoSchema,
          employees: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
                employeeCode: z.string().optional(),
              }),
            )
            .optional(),
          shifts: z
            .array(
              z.object({
                id: z.string(),
                code: z.string(),
                name: z.string(),
                startTime: z.string(),
                endTime: z.string(),
              }),
            )
            .optional(),
          departments: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
          locations: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Get daily employee absent/present roster and category summary counts',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/attendance' },
);
